import React, { useState, useEffect, useRef } from 'react';
import {
  Send,
  Phone,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  RefreshCw,
  Users,
  Search,
  Shield,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  UserPlus,
  History,
  Star,
  X,
  Download,
  Trash2,
  Ban,
  Image as ImageIcon,
  FileImage,
  Mail,
  Newspaper,
  Film,
  Layout,
  Zap,
  Instagram,
  Facebook,
  Music,
  Smartphone,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  Paperclip,
  Tag,
  Gem,
  MapPin
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import FlyerBuilder from './FlyerBuilder';
import CatalogProductPicker from './CatalogProductPicker';
import VideoStudio, { IngredientSlots, processIngredientFile, processFootageFile, MAX_FOOTAGE_FILES, MAX_FOOTAGE_MB, FOOTAGE_LENGTHS, useVideoGeneration, useSavedIngredients, PROGRESS_LINES, VideoStyleControls, RecentVideosStrip, CaptionsHealthBanner } from './VideoStudio';

/**
 * MarketingTab — AI Design Studio
 *
 * The promotion surface is reframed as a premium AI design studio.
 * The prompt IS the page. Format pills telegraph the broader vision
 * (Flyer + Text active today; Social, Email, Postcard, Story Ad coming).
 *
 * Layout states:
 *   - design: hero with format pills + FlyerBuilder (or text composer)
 *   - send:   flyer preview + inline send card (caption, audience, send)
 *
 * Drawers retained for Contacts and History — secondary surfaces.
 */

// ==========================================
// SHARED PRIMITIVES
// ==========================================
const LEGAL_FOOTER = '\n\nReply STOP to opt out. Reply HELP for help. Msg & data rates may apply.';

const formatPhoneInput = (value) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const getE164Phone = (formatted) => {
  const digits = formatted.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return `+${digits}`;
};

const isValidPhone = (formatted) => {
  const digits = formatted.replace(/\D/g, '');
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
};

const getSurveyStatusBadge = (survey) => {
  if (survey.customer_satisfaction_score) {
    const score = survey.customer_satisfaction_score;
    return (
      <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold ${
        score >= 4 ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'
      }`}>
        <Star className="w-2.5 h-2.5 fill-current" /><span>{score}/5</span>
      </div>
    );
  }
  if (survey.survey_status === 'in_progress' || survey.survey_status === 'sent') {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-amber-50 border border-amber-200 text-amber-700">
        <Clock className="w-2.5 h-2.5" /><span>Pending</span>
      </div>
    );
  }
  return (
    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-slate-100 border border-slate-200 text-slate-600">
      <Send className="w-2.5 h-2.5" /><span>Sent</span>
    </div>
  );
};

const ResultBanner = ({ result }) => {
  if (!result) return null;
  return (
    <div className={`flex items-center gap-2.5 px-4 py-2.5 rounded-lg border ${
      result.type === 'success' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
    }`}>
      {result.type === 'success'
        ? <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
        : <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
      }
      <span className={`text-sm font-medium ${result.type === 'success' ? 'text-emerald-700' : 'text-red-700'}`}>
        {result.message}
      </span>
    </div>
  );
};

const PhoneInputWithSend = ({ phone, setPhone, onSend, sending, result, placeholder = '(555) 123-4567', label = 'Send' }) => (
  <div className="space-y-2.5">
    <div className="flex gap-2">
      <div className="relative flex-1">
        <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
          onKeyDown={(e) => { if (e.key === 'Enter' && isValidPhone(phone)) onSend(); }}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-2.5 rounded-lg bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all text-sm"
          maxLength={14}
        />
        {phone && (
          <button onClick={() => setPhone('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors">
            <X className="w-3 h-3 text-slate-500" />
          </button>
        )}
      </div>
      <button
        onClick={onSend}
        disabled={sending || !isValidPhone(phone)}
        className={`px-4 py-2.5 rounded-lg font-semibold text-sm flex items-center gap-1.5 transition-all ${
          sending || !isValidPhone(phone)
            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
            : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
        }`}
      >
        {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        <span className="hidden sm:inline">{sending ? 'Sending' : label}</span>
      </button>
    </div>
    <ResultBanner result={result} />
  </div>
);

const Drawer = ({ open, onClose, title, subtitle, icon: Icon, iconTone = 'blue', children, width = 'md', footer = null }) => {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const widths = { sm: 'sm:max-w-sm', md: 'sm:max-w-md', lg: 'sm:max-w-lg', xl: 'sm:max-w-xl', '2xl': 'sm:max-w-2xl' };
  const tones = {
    blue:    'bg-blue-50 border-blue-200 text-blue-600',
    amber:   'bg-amber-50 border-amber-200 text-amber-600',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-600',
    slate:   'bg-slate-100 border-slate-200 text-slate-600',
    red:     'bg-red-50 border-red-200 text-red-600'
  };

  return (
    <div className={`fixed inset-0 z-50 transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} aria-hidden={!open}>
      <div onClick={onClose} className={`absolute inset-0 bg-slate-900/40 backdrop-blur-[2px] transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'}`} />
      <div className={`absolute right-0 top-0 bottom-0 w-full ${widths[width] || widths.md} bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex-shrink-0 flex items-start justify-between gap-3 px-5 pb-4 pt-[max(16px,env(safe-area-inset-top))] border-b border-slate-200">
          <div className="flex items-center gap-3 min-w-0">
            {Icon && (
              <div className={`w-10 h-10 rounded-xl border flex items-center justify-center flex-shrink-0 ${tones[iconTone]}`}>
                <Icon className="w-5 h-5" />
              </div>
            )}
            <div className="min-w-0">
              <h3 className="text-base font-bold text-slate-900 truncate">{title}</h3>
              {subtitle && <p className="text-xs text-slate-500 truncate">{subtitle}</p>}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-all flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className={`flex-1 overflow-y-auto p-5 ${footer ? '' : 'pb-[max(20px,env(safe-area-inset-bottom))]'}`}>{children}</div>
        {footer && (
          <div className="flex-shrink-0 px-5 pt-3.5 pb-[max(14px,env(safe-area-inset-bottom))] border-t border-slate-200 bg-slate-50/60">{footer}</div>
        )}
      </div>
    </div>
  );
};

// ==========================================
// TEXT PROMO SPLIT — two-pane text composer matching the FlyerBuilder split layout.
// Left pane: input + suggestions. Right pane: live SMS bubble preview.
// ==========================================
const TEXT_PROMPT_SUGGESTIONS = [
  '🎉 Memorial Day Weekend — 30% off everything in store!',
  '🛏️ New mattress shipment just arrived — preview tonight 6–8pm',
  '🚚 Free delivery on any order over $999 this week',
  '💥 Clearance event Saturday — first 20 customers get a $50 gift card',
];

const TextPromoSplit = ({ promoMessage, setPromoMessage, onContinue }) => (
  <div className="grid grid-cols-1 md:grid-cols-[minmax(0,400px)_1fr] gap-4 h-full min-h-0">

    {/* LEFT — composer */}
    <div className="flex flex-col min-h-0 h-full">
      <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <div>
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider leading-none">Text Composer</p>
            <p className="text-[11px] text-slate-400 leading-tight mt-0.5">Quick SMS — no image, instant delivery</p>
          </div>
        </div>

        <div className="relative group">
          <div className="absolute -inset-px bg-gradient-to-br from-blue-500/30 via-indigo-500/20 to-purple-500/30 rounded-2xl opacity-0 group-focus-within:opacity-100 blur-md transition-opacity duration-500" />
          <div className="relative bg-white border border-slate-200 group-focus-within:border-blue-300 rounded-2xl shadow-sm transition-colors overflow-hidden">
            <textarea
              value={promoMessage}
              onChange={(e) => setPromoMessage(e.target.value)}
              placeholder="Write your text — &ldquo;🎉 Memorial Day weekend — 30% off everything in store. Show this text at checkout!&rdquo;"
              className="w-full h-32 px-4 pt-4 pb-2 bg-transparent text-[14px] text-slate-900 placeholder-slate-400 resize-none focus:outline-none leading-relaxed"
              maxLength={500}
            />
            <div className="flex items-center justify-between gap-3 px-3 pb-2.5 pt-1 border-t border-slate-100">
              <div className="flex items-center gap-1.5 text-[10.5px] text-slate-400">
                <Shield className="w-3 h-3" />
                <span>STOP/HELP footer auto-appended</span>
              </div>
              <span className="text-[10.5px] text-slate-400 tabular-nums">{promoMessage.length}/500</span>
            </div>
          </div>
        </div>

        {!promoMessage && (
          <div>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 ml-1">Try one of these</p>
            <div className="flex flex-wrap gap-1.5">
              {TEXT_PROMPT_SUGGESTIONS.map((prompt, i) => (
                <button
                  key={i}
                  onClick={() => setPromoMessage(prompt)}
                  className="px-2.5 py-1 bg-white hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded-full text-[11px] text-slate-600 hover:text-blue-700 transition-all shadow-sm text-left"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex-shrink-0 pt-2.5 mt-2 border-t border-slate-200">
        <button
          onClick={onContinue}
          disabled={!promoMessage.trim()}
          className={`w-full py-3.5 rounded-2xl font-semibold text-[13px] flex items-center justify-center gap-2 transition-all ${
            !promoMessage.trim()
              ? 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed'
              : 'bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-600/20'
          }`}
        >
          <span>Continue to send</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>

    {/* RIGHT — SMS bubble preview */}
    <div className="relative bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-2xl overflow-hidden h-full min-h-[420px] flex items-center justify-center p-6">
      <div
        className="absolute inset-0 opacity-[0.4] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, #cbd5e1 1px, transparent 1px)',
          backgroundSize: '20px 20px',
        }}
      />

      {!promoMessage.trim() ? (
        <div className="relative flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center mb-3">
            <MessageSquare className="w-6 h-6 text-slate-300" />
          </div>
          <h3 className="text-[14px] font-bold text-slate-700 mb-1">Your message preview</h3>
          <p className="text-[12px] text-slate-500 max-w-[260px] leading-relaxed">
            Type your text on the left to see how it lands on a customer&apos;s phone.
          </p>
        </div>
      ) : (
        <div className="relative w-full max-w-[320px]">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex items-center justify-between">
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">SMS Preview</span>
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span className="text-[10px] text-slate-500">live</span>
              </div>
            </div>
            <div className="p-4 space-y-2">
              <div className="bg-slate-100 rounded-2xl rounded-tl-md px-4 py-2.5 max-w-[95%]">
                <p className="text-[13px] text-slate-800 leading-relaxed whitespace-pre-wrap">{promoMessage}</p>
              </div>
              <div className="bg-slate-50 rounded-2xl rounded-tl-md px-4 py-2 max-w-[95%]">
                <p className="text-[10.5px] text-slate-400 italic leading-snug">
                  Reply STOP to opt out. Reply HELP for help. Msg &amp; data rates may apply.
                </p>
              </div>
              <p className="text-[10px] text-slate-400 text-center pt-1 tabular-nums">
                {promoMessage.length + 88} characters total ·{' '}
                {Math.ceil((promoMessage.length + 88) / 160)} segment{Math.ceil((promoMessage.length + 88) / 160) === 1 ? '' : 's'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
);

// ==========================================
// FORMAT CATALOG — Canva-style. Each format carries the dimensions, platform,
// and delivery mode that downstream logic needs.
//
// `deliveryMode`:
//   'sms'    — captured PNG is uploaded to Supabase, then SMS/MMS to customers
//   'social' — captured PNG downloads to the user's device for manual posting
//
// `width` / `height` — the native canvas size the AI should design at and the
// final PNG dimensions. `null` height = variable (legacy flyer behaviour).
// ==========================================
const FORMATS = [
  {
    // "AI Video Ad" — Veo 3.1 generation, not an HTML canvas. Routes to
    // VideoStudio instead of FlyerBuilder via the isVideo flag. The user
    // supplies up to 3 reference photos (person / store / product) and a
    // description; the output is a real 8s vertical MP4 with spoken
    // dialogue and music, ready for TikTok / Reels / Shorts.
    // FIRST in the array on purpose — it leads the format picker. The
    // DEFAULT selection is still 'flyer' (set by id in useState), and the
    // findFormat fallback pins to 'flyer' explicitly, so ordering here is
    // purely presentational.
    id: 'ai-video',
    label: 'AI Video Ad',
    sublabel: 'Real 8s video · person + store + product',
    group: 'vertical',
    icon: Film,
    accent: 'blue',
    accentBg: 'from-blue-100 to-blue-200',
    accentBorder: 'hover:border-blue-400',
    accentText: 'text-blue-700',
    accentIconBg: 'bg-blue-50',
    width: 1080,
    height: 1920,
    aspectRatio: '9/16',
    platform: 'video',
    deliveryMode: 'social',
    live: true,
    isVideo: true,
  },
  // — Send to customers —
  {
    id: 'flyer',
    label: 'Flyer',
    sublabel: 'Print or send via MMS',
    group: 'send',
    icon: FileImage,
    accent: 'slate',
    accentBg: 'from-slate-100 to-slate-200',
    accentBorder: 'hover:border-slate-400',
    accentText: 'text-slate-700',
    accentIconBg: 'bg-slate-100',
    width: 600,
    height: null,
    aspectRatio: '3/4',
    platform: 'general',
    deliveryMode: 'sms',
    live: true,
  },
  {
    // "Current Deal" — same canvas as the flyer, but tagged so it lands in
    // the phone agent's FAQ as an active promotion. The Flyer Builder uses
    // this format_id, /api/designs sets is_current_deal=true on save, and
    // the agent prompt + send_flyer tool prefer these when callers ask
    // about sales/specials.
    id: 'current-deal',
    label: 'Current Deal',
    sublabel: 'Auto-publishes to your phone agent FAQ',
    group: 'send',
    icon: Tag,
    accent: 'amber',
    accentBg: 'from-amber-100 to-orange-200',
    accentBorder: 'hover:border-amber-400',
    accentText: 'text-amber-700',
    accentIconBg: 'bg-amber-50',
    width: 600,
    height: null,
    aspectRatio: '3/4',
    platform: 'general',
    deliveryMode: 'sms',
    live: true,
    isCurrentDeal: true,
  },
  {
    id: 'text',
    label: 'Text Promo',
    sublabel: 'Quick SMS message',
    group: 'send',
    icon: MessageSquare,
    accent: 'sky',
    accentBg: 'from-sky-100 to-sky-200',
    accentBorder: 'hover:border-sky-400',
    accentText: 'text-sky-700',
    accentIconBg: 'bg-sky-50',
    isText: true,
    platform: 'sms',
    deliveryMode: 'sms',
    live: true,
  },

  // — Square social posts —
  {
    id: 'ig-post',
    label: 'Instagram Post',
    sublabel: 'Square 1:1 · 1080×1080',
    group: 'square',
    icon: Instagram,
    accent: 'pink',
    accentBg: 'from-pink-100 via-rose-100 to-orange-100',
    accentBorder: 'hover:border-pink-400',
    accentText: 'text-pink-700',
    accentIconBg: 'bg-pink-50',
    width: 1080,
    height: 1080,
    aspectRatio: '1/1',
    platform: 'instagram',
    deliveryMode: 'social',
    live: true,
  },
  {
    id: 'fb-post',
    label: 'Facebook Post',
    sublabel: 'Square 1:1 · 1200×1200',
    group: 'square',
    icon: Facebook,
    accent: 'blue',
    accentBg: 'from-blue-100 to-indigo-200',
    accentBorder: 'hover:border-blue-400',
    accentText: 'text-blue-700',
    accentIconBg: 'bg-blue-50',
    width: 1200,
    height: 1200,
    aspectRatio: '1/1',
    platform: 'facebook',
    deliveryMode: 'social',
    live: true,
  },

  // — Vertical stories & reels —
  {
    id: 'ig-story',
    label: 'Instagram Story',
    sublabel: 'Vertical 9:16 · 1080×1920',
    group: 'vertical',
    icon: Instagram,
    accent: 'pink',
    accentBg: 'from-pink-100 via-rose-100 to-orange-100',
    accentBorder: 'hover:border-pink-400',
    accentText: 'text-pink-700',
    accentIconBg: 'bg-pink-50',
    width: 1080,
    height: 1920,
    aspectRatio: '9/16',
    platform: 'instagram',
    deliveryMode: 'social',
    live: true,
  },
  {
    id: 'fb-story',
    label: 'Facebook Story',
    sublabel: 'Vertical 9:16 · 1080×1920',
    group: 'vertical',
    icon: Facebook,
    accent: 'blue',
    accentBg: 'from-blue-100 to-indigo-200',
    accentBorder: 'hover:border-blue-400',
    accentText: 'text-blue-700',
    accentIconBg: 'bg-blue-50',
    width: 1080,
    height: 1920,
    aspectRatio: '9/16',
    platform: 'facebook',
    deliveryMode: 'social',
    live: true,
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    sublabel: 'Vertical 9:16 · 1080×1920',
    group: 'vertical',
    icon: Music,
    accent: 'rose',
    accentBg: 'from-rose-100 via-fuchsia-100 to-cyan-100',
    accentBorder: 'hover:border-rose-400',
    accentText: 'text-rose-700',
    accentIconBg: 'bg-rose-50',
    width: 1080,
    height: 1920,
    aspectRatio: '9/16',
    platform: 'tiktok',
    deliveryMode: 'social',
    live: true,
  },
  {
    id: 'reel',
    label: 'Reel / Short',
    sublabel: 'Vertical 9:16 · 1080×1920',
    group: 'vertical',
    icon: Film,
    accent: 'purple',
    accentBg: 'from-purple-100 to-fuchsia-200',
    accentBorder: 'hover:border-purple-400',
    accentText: 'text-purple-700',
    accentIconBg: 'bg-purple-50',
    width: 1080,
    height: 1920,
    aspectRatio: '9/16',
    platform: 'video',
    deliveryMode: 'social',
    live: true,
  },
];

const FORMAT_GROUPS = [
  {
    id: 'send',
    label: 'Send to customers',
    desc: 'Reach your customers directly',
    icon: Send,
  },
  {
    id: 'square',
    label: 'Square social posts',
    desc: 'For feeds on Instagram & Facebook',
    icon: Layout,
  },
  {
    id: 'vertical',
    label: 'Stories & Reels',
    desc: 'Tall vertical for stories, reels & TikTok',
    icon: Smartphone,
  },
];

// Resolves a format object from id; falls back to flyer if not found.
const findFormat = (id) => FORMATS.find(f => f.id === id) || FORMATS.find(f => f.id === 'flyer') || FORMATS[0];

// ==========================================
// FORMAT CARD — used in the picker. Shows a true-aspect-ratio thumbnail with
// platform-tinted gradient so the user *sees* the shape they're making.
// ==========================================
const FormatCard = ({ format: fmt, onClick, onMouseEnter, onMouseLeave, isPreviewing = false }) => {
  const Icon = fmt.icon;
  return (
    <button
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`group relative flex flex-col p-2.5 bg-white rounded-xl border ${isPreviewing ? 'border-blue-400 ring-2 ring-blue-200' : 'border-slate-200'} ${fmt.accentBorder} hover:shadow-md hover:-translate-y-0.5 transition-all text-left w-full`}
    >
      {/* Aspect-ratio thumbnail — compact */}
      <div className="relative bg-slate-50 rounded-lg flex items-center justify-center mb-2 h-16 overflow-hidden">
        {fmt.isText ? (
          <div className="w-full max-w-[100px] flex flex-col gap-1 px-2">
            <div className="h-1 w-3/4 rounded-full bg-slate-300" />
            <div className="h-1 w-full rounded-full bg-slate-300" />
            <div className="h-1 w-5/6 rounded-full bg-slate-300" />
            <div className="mt-0.5 flex justify-end">
              <div className={`h-2.5 w-5 rounded-full bg-gradient-to-br ${fmt.accentBg}`} />
            </div>
          </div>
        ) : (
          <div
            className={`bg-gradient-to-br ${fmt.accentBg} rounded shadow-sm transition-transform group-hover:scale-105 flex items-center justify-center`}
            style={{
              aspectRatio: fmt.aspectRatio || '3/4',
              maxHeight: '85%',
              maxWidth: '85%',
              height: '100%',
            }}
          >
            <Icon className={`w-3 h-3 ${fmt.accentText} opacity-60`} />
          </div>
        )}
      </div>

      {/* Compact label row */}
      <div className="flex items-start gap-1.5 min-w-0">
        <div className={`flex-shrink-0 w-5 h-5 rounded-md ${fmt.accentIconBg} border border-slate-200 flex items-center justify-center`}>
          <Icon className={`w-2.5 h-2.5 ${fmt.accentText}`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold text-slate-900 truncate leading-tight">{fmt.label}</p>
          <p className="text-[9.5px] text-slate-500 truncate leading-tight mt-0.5">{fmt.sublabel}</p>
        </div>
      </div>
    </button>
  );
};

// ==========================================
// SCALE TO FIT — measures available space and scales a fixed-size child down
// (never up past 1) to fit without clipping or distorting. The child keeps
// its exact dimensions/aspect; only a CSS transform shrinks it.
// ==========================================
const ScaleToFit = ({ baseWidth, baseHeight, children, className = '', maxScale = 1 }) => {
  const boxRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      if (width <= 0 || height <= 0) return;
      const next = Math.min(width / baseWidth, height / baseHeight, maxScale);
      setScale(next > 0 ? next : 1);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [baseWidth, baseHeight, maxScale]);

  return (
    <div ref={boxRef} className={`w-full h-full flex items-center justify-center ${className}`}>
      <div style={{ width: baseWidth * scale, height: baseHeight * scale }}>
        <div style={{ width: baseWidth, height: baseHeight, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

// ==========================================
// DEVICE PREVIEW — phone mockup that renders the format's shape inside a screen
// ==========================================
const PhoneFrame = ({ children }) => (
  <div
    className="relative bg-gradient-to-br from-slate-500 to-slate-700 rounded-[2.25rem] p-1.5 shadow-2xl ring-1 ring-slate-400/40"
    style={{ width: 300, height: 620 }}
  >
    {/* Notch */}
    <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-20 h-5 bg-slate-700 rounded-b-2xl z-20 flex items-center justify-center gap-1.5">
      <div className="w-1 h-1 rounded-full bg-slate-500" />
      <div className="w-6 h-1 rounded-full bg-slate-500" />
    </div>
    {/* Screen */}
    <div className="relative w-full h-full bg-white rounded-[1.85rem] overflow-hidden flex flex-col">
      {children}
    </div>
  </div>
);

const StatusBar = ({ tone = 'dark' }) => (
  <div className={`flex-shrink-0 h-7 flex items-center justify-between px-5 pt-1.5 text-[9px] font-bold tabular-nums z-10 ${tone === 'light' ? 'text-white' : 'text-slate-900'}`}>
    <span>9:41</span>
    <div className="flex items-center gap-1">
      <div className={`w-3 h-1.5 rounded-[2px] border ${tone === 'light' ? 'border-white/90' : 'border-slate-900'}`}>
        <div className={`h-full w-2/3 ${tone === 'light' ? 'bg-white/90' : 'bg-slate-900'} rounded-[1px]`} />
      </div>
    </div>
  </div>
);

// ==========================================
// HERO VIDEO PHONE — replaces DevicePreview in the picker's right pane while
// an AI Video generation is running / finished, so the WHOLE flow (compose ->
// render -> watch -> download) happens on the first screen. Sized 300x620 to
// drop into the same ScaleToFit slot DevicePreview uses.
// ==========================================
// ==========================================
// SAVED DESIGN CARD — one thumbnail in the library strip. Extracted so the
// grouped library ("Flyers & deals" / "Social posts") reuses one card.
// ==========================================
const SavedDesignCard = ({ d, onLoad, onDelete }) => {
  const isVertical = d.format_height && d.format_height > d.format_width;
  const isSquare = d.format_height && d.format_height === d.format_width;
  const aspect = isVertical ? '9/16' : isSquare ? '1/1' : '3/4';
  const ageMs = Date.now() - new Date(d.created_at).getTime();
  const age =
    ageMs < 60_000 ? 'now'
    : ageMs < 3_600_000 ? `${Math.floor(ageMs / 60_000)}m`
    : ageMs < 86_400_000 ? `${Math.floor(ageMs / 3_600_000)}h`
    : `${Math.floor(ageMs / 86_400_000)}d`;

  const thumbHeight = 96;
  const thumbWidth = aspect === '9/16'
    ? Math.round(thumbHeight * 9 / 16)
    : aspect === '1/1'
    ? thumbHeight
    : Math.round(thumbHeight * 3 / 4);

  return (
    <button
      onClick={() => onLoad(d)}
      className="group relative rounded-md overflow-hidden border border-slate-200 hover:border-blue-300 bg-white shadow-sm hover:shadow-md transition-all flex-shrink-0"
      style={{ width: `${thumbWidth}px`, height: `${thumbHeight}px` }}
      title={d.description || d.format_label}
    >
      {d.thumbnail_url ? (
        <img src={d.thumbnail_url} alt="" loading="lazy" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-slate-300 bg-slate-50">
          <Sparkles className="w-3 h-3" />
        </div>
      )}
      <button
        type="button"
        onClick={(e) => onDelete(d.id, e)}
        title="Delete design"
        className="absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-white/95 border border-slate-200 hover:bg-red-50 hover:border-red-200 hover:text-red-600 text-slate-500 shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <X className="w-2 h-2" strokeWidth={2.5} />
      </button>
      <span className="absolute bottom-0.5 right-0.5 px-1 py-px rounded bg-black/55 text-white text-[8px] font-semibold leading-none">
        {age}
      </span>
    </button>
  );
};

const HeroVideoPhone = ({ gen, businessName }) => {
  const fmtElapsed = `${Math.floor(gen.elapsed / 60)}:${String(gen.elapsed % 60).padStart(2, '0')}`;
  return (
    <div
      className="relative rounded-[2rem] border-[6px] border-slate-900 bg-slate-950 shadow-xl overflow-hidden flex flex-col"
      style={{ width: 300, height: 620 }}
    >
      {gen.phase === 'generating' && (
        <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
          <div className="relative">
            <div className="w-14 h-14 rounded-full border-4 border-blue-500/20 border-t-blue-500 animate-spin" />
            <Film className="w-5 h-5 text-blue-400 absolute inset-0 m-auto" />
          </div>
          <p className="text-sm font-bold text-white">{PROGRESS_LINES[gen.progressIdx]}</p>
          <div className="flex items-center gap-1.5 text-xs text-slate-400">
            <Clock className="w-3.5 h-3.5" />
            <span>{fmtElapsed} · usually 1–4 min</span>
          </div>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            You can keep working — this finishes in the background and lands in Recent videos.
          </p>
        </div>
      )}

      {gen.phase === 'ready' && gen.videoUrl && (
        <>
          <video
            key={`${gen.videoUrl}::${gen.playToken ?? 0}`}
            src={gen.videoUrl}
            controls
            playsInline
            loop
            className="flex-1 min-h-0 w-full object-cover bg-black"
          />
          <div className="flex gap-2 p-2 bg-slate-950 flex-shrink-0">
            <a
              href={gen.videoUrl}
              download={`${(businessName || 'video').replace(/\s+/g, '_')}_ad.mp4`}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white hover:bg-slate-100 text-slate-900 text-xs font-bold transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Download
            </a>
            <button
              onClick={gen.reset}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-slate-700 hover:bg-slate-800 text-slate-200 text-xs font-bold transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              New
            </button>
          </div>
        </>
      )}

      {gen.phase === 'failed' && (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-400" />
          <p className="text-sm font-bold text-white">That one didn’t make it</p>
          <p className="text-xs text-slate-400 leading-relaxed">{gen.error}</p>
          <button
            onClick={gen.reset}
            className="mt-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-slate-700 hover:bg-slate-800 text-slate-200 text-xs font-bold transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Try again
          </button>
        </div>
      )}
    </div>
  );
};

const DevicePreview = ({ format }) => {
  // ===== Empty state — no format hovered =====
  if (!format) {
    return (
      <PhoneFrame>
        <StatusBar />
        <div className="flex-1 flex flex-col items-center justify-center px-5 text-center">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 flex items-center justify-center mb-3">
            <Sparkles className="w-5 h-5 text-slate-300" />
          </div>
          <p className="text-[11px] font-semibold text-slate-700 leading-snug">
            Hover a format
          </p>
          <p className="text-[10px] text-slate-400 mt-1 leading-snug">
            See how it will look on a phone
          </p>
        </div>
      </PhoneFrame>
    );
  }

  const Icon = format.icon;

  // ===== Text Promo — SMS conversation =====
  if (format.isText) {
    return (
      <PhoneFrame>
        <StatusBar />
        {/* Conversation header */}
        <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 border-b border-slate-100">
          <ArrowLeft className="w-3 h-3 text-blue-500" />
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-slate-200 to-slate-300" />
          <div className="flex-1">
            <div className="h-1.5 w-16 rounded-full bg-slate-300" />
            <div className="h-1 w-10 rounded-full bg-slate-200 mt-0.5" />
          </div>
        </div>
        {/* Bubbles */}
        <div className="flex-1 flex flex-col gap-2 p-3 overflow-hidden">
          <div className="self-start max-w-[78%] bg-slate-100 rounded-2xl rounded-bl-sm px-3 py-2 space-y-1">
            <div className="h-1.5 w-20 rounded-full bg-slate-300" />
            <div className="h-1.5 w-28 rounded-full bg-slate-300" />
          </div>
          <div className="self-end max-w-[78%] bg-blue-500 rounded-2xl rounded-br-sm px-3 py-2 space-y-1">
            <div className="h-1.5 w-24 rounded-full bg-blue-200" />
            <div className="h-1.5 w-16 rounded-full bg-blue-200" />
          </div>
          <div className="self-start max-w-[60%] bg-slate-100 rounded-2xl rounded-bl-sm px-3 py-2">
            <div className="h-1.5 w-14 rounded-full bg-slate-300" />
          </div>
        </div>
        {/* Input bar */}
        <div className="flex-shrink-0 px-3 pb-3 pt-1">
          <div className="h-6 rounded-full bg-slate-100 border border-slate-200" />
        </div>
      </PhoneFrame>
    );
  }

  // ===== Vertical 9:16 — Stories / Reels (full-bleed) =====
  if (format.aspectRatio === '9/16') {
    return (
      <PhoneFrame>
        {/* Full-bleed gradient background */}
        <div className={`absolute inset-0 bg-gradient-to-br ${format.accentBg}`} />
        {/* Story progress bars */}
        <div className="relative z-10 flex gap-1 px-3 pt-2.5">
          <div className="flex-1 h-0.5 bg-white/90 rounded-full" />
          <div className="flex-1 h-0.5 bg-white/35 rounded-full" />
          <div className="flex-1 h-0.5 bg-white/35 rounded-full" />
        </div>
        {/* Top user row */}
        <div className="relative z-10 flex items-center gap-2 px-3 mt-2">
          <div className="w-5 h-5 rounded-full bg-white/90 ring-1 ring-white/60" />
          <div className="h-1.5 w-12 rounded-full bg-white/80" />
        </div>
        {/* Centered icon — represents the design */}
        <div className="relative z-10 flex-1 flex items-center justify-center">
          <Icon className={`w-14 h-14 ${format.accentText} opacity-60`} />
        </div>
        {/* Bottom action row */}
        <div className="relative z-10 flex items-center gap-2 px-3 pb-3">
          <div className="flex-1 h-7 rounded-full bg-white/20 border border-white/40" />
          <div className="w-5 h-5 rounded-full bg-white/30" />
        </div>
      </PhoneFrame>
    );
  }

  // ===== Square 1:1 — Instagram/Facebook Post (feed view) =====
  if (format.aspectRatio === '1/1') {
    return (
      <PhoneFrame>
        <StatusBar />
        {/* Feed app top bar */}
        <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b border-slate-100">
          <div className="h-1.5 w-12 rounded-full bg-slate-300" />
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full border border-slate-300" />
            <div className="w-3 h-3 rounded-full border border-slate-300" />
          </div>
        </div>
        {/* Post header */}
        <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2">
          <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${format.accentBg} ring-1 ring-slate-200`} />
          <div className="flex-1">
            <div className="h-1.5 w-16 rounded-full bg-slate-300" />
            <div className="h-1 w-10 rounded-full bg-slate-200 mt-0.5" />
          </div>
          <div className="text-slate-300 text-[10px]">···</div>
        </div>
        {/* Square content */}
        <div className={`relative w-full bg-gradient-to-br ${format.accentBg} flex items-center justify-center`} style={{ aspectRatio: '1/1' }}>
          <Icon className={`w-12 h-12 ${format.accentText} opacity-60`} />
        </div>
        {/* Action row */}
        <div className="flex-shrink-0 flex items-center gap-2.5 px-3 py-2">
          <div className="w-3.5 h-3.5 rounded-full border-[1.5px] border-slate-400" />
          <div className="w-3.5 h-3.5 rounded-full border-[1.5px] border-slate-400" />
          <div className="w-3.5 h-3.5 rounded-full border-[1.5px] border-slate-400" />
        </div>
      </PhoneFrame>
    );
  }

  // ===== Default (Flyer 3:4) — show as MMS attachment in messages =====
  return (
    <PhoneFrame>
      <StatusBar />
      {/* Conversation header */}
      <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 border-b border-slate-100">
        <ArrowLeft className="w-3 h-3 text-blue-500" />
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-slate-200 to-slate-300" />
        <div className="flex-1">
          <div className="h-1.5 w-20 rounded-full bg-slate-300" />
          <div className="h-1 w-10 rounded-full bg-slate-200 mt-0.5" />
        </div>
      </div>
      {/* Body — image attachment + caption */}
      <div className="flex-1 flex flex-col p-3 gap-2 overflow-hidden">
        <div className="self-start max-w-[60%] bg-slate-100 rounded-2xl rounded-bl-sm px-3 py-2 space-y-1">
          <div className="h-1.5 w-16 rounded-full bg-slate-300" />
          <div className="h-1.5 w-20 rounded-full bg-slate-300" />
        </div>
        {/* Flyer image attachment */}
        <div className="self-start" style={{ width: '70%' }}>
          <div className={`bg-gradient-to-br ${format.accentBg} rounded-2xl flex items-center justify-center shadow-sm`} style={{ aspectRatio: '3/4' }}>
            <Icon className={`w-12 h-12 ${format.accentText} opacity-60`} />
          </div>
        </div>
      </div>
      {/* Input bar */}
      <div className="flex-shrink-0 px-3 pb-3 pt-1">
        <div className="h-6 rounded-full bg-slate-100 border border-slate-200" />
      </div>
    </PhoneFrame>
  );
};

// ==========================================
// FORMAT GROUP ROW — horizontal scroller with arrows
// Always shows up to 3 cards across; if more, arrow buttons slide through.
// ==========================================
const FormatGroupRow = ({ group, formats, hoveredFormat, onSelect, onHover, onLeave }) => {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const GroupIcon = group.icon;

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    const hasOverflow = el.scrollWidth > el.clientWidth + 1;
    setShowLeft(hasOverflow && el.scrollLeft > 4);
    setShowRight(hasOverflow && el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [formats.length]);

  const scrollByCard = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    // Width of one card slot: container width / 3 (we always show 3 across)
    const step = el.clientWidth / 3;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <div>
      {/* Group header */}
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-4 rounded bg-slate-100 border border-slate-200 flex items-center justify-center">
            <GroupIcon className="w-2.5 h-2.5 text-slate-500" />
          </div>
          <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{group.label}</h3>
          {formats.length > 3 && (
            <span className="text-[10px] font-semibold text-slate-400">({formats.length})</span>
          )}
        </div>
        {formats.length > 3 && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => scrollByCard(-1)}
              disabled={!showLeft}
              aria-label="Scroll left"
              className="w-6 h-6 rounded-md border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => scrollByCard(1)}
              disabled={!showRight}
              aria-label="Scroll right"
              className="w-6 h-6 rounded-md border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Horizontal scroller — always shows exactly 3 cards across; rest slide in via arrows */}
      <div
        ref={scrollRef}
        className="flex gap-2 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {formats.map((fmt) => (
          <div
            key={fmt.id}
            className="snap-start flex-shrink-0"
            style={{ width: 'calc((100% - 1rem) / 3)' }}
          >
            <FormatCard
              format={fmt}
              onClick={() => onSelect(fmt)}
              onMouseEnter={() => onHover(fmt)}
              onMouseLeave={onLeave}
              isPreviewing={hoveredFormat?.id === fmt.id}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// ==========================================
// STUDIO COMPOSER — chat-first home hero.
// The prompt is the primary action; format is a chip detail on the composer.
// Replaces the old "pick a card first" gallery. The quick chips show the three
// live SMS-deliverable formats; the "Social" dropdown holds the rest.
// ==========================================
const PROMPT_IDEAS = [
  'Weekend sale — 25% off everything',
  'New arrival just landed',
  'Free local delivery this month',
  'Clearance event — everything must go',
];

// FOOTAGE UPLOADER — the whole input for "Edit My Video": the user's own
// clips plus how long the finished cut should run. Deliberately NOT the
// IngredientSlots component: those are reference PHOTOS for a generated
// video, and reusing them here would imply the footage is a style
// reference rather than the actual material.
const FootageUploader = ({ files, onAdd, onClear, disabled, seconds, setSeconds }) => {
  const inputRef = useRef(null);
  return (
    <>
      <div className="rounded-xl border-2 border-dashed border-slate-200 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <Film className="w-4 h-4 flex-shrink-0 text-slate-400" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-slate-700">Your footage</p>
            <p className="text-[10px] text-slate-400">
              Up to {MAX_FOOTAGE_FILES} clips, {MAX_FOOTAGE_MB}MB each. MP4 or MOV, any orientation.
              {files.length > 1 ? ' They’ll be cut together into one video.' : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={disabled || files.length >= MAX_FOOTAGE_FILES}
            className="flex-shrink-0 px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-blue-600 border border-blue-200 hover:bg-blue-50 disabled:opacity-50 transition-colors"
          >
            {files.length ? 'Add another' : 'Add video'}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="video/*"
            multiple
            className="hidden"
            onChange={(e) => {
              // Multi-select in one go is the common case when someone
              // shot three clips on their phone.
              Array.from(e.target.files || []).forEach((f) => onAdd(f));
              e.target.value = '';
            }}
          />
        </div>

        {files.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {files.map((f, i) => (
              <div key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-lg bg-slate-50 px-2 py-1.5">
                <span className="flex-shrink-0 w-4 h-4 rounded bg-blue-100 text-blue-700 text-[9px] font-bold flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="flex-1 min-w-0 truncate text-[11px] font-medium text-slate-700">{f.name}</span>
                <span className="flex-shrink-0 text-[10px] text-slate-400">{f.sizeMb}MB</span>
                <button
                  type="button"
                  onClick={() => onClear(i)}
                  disabled={disabled}
                  className="flex-shrink-0 text-slate-400 hover:text-red-600 disabled:opacity-50"
                  aria-label={`Remove ${f.name}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-3">
        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">
          How long should the cut be?
        </p>
        <div className="flex gap-2">
          {FOOTAGE_LENGTHS.map((secs) => (
            <button
              key={secs}
              type="button"
              onClick={() => setSeconds(secs)}
              disabled={disabled}
              className={`flex-1 px-2 py-2 rounded-xl border-2 text-xs font-bold transition-colors disabled:opacity-50 ${
                seconds === secs
                  ? 'border-blue-500 bg-blue-50 text-blue-900'
                  : 'border-slate-200 hover:border-slate-300 text-slate-700'
              }`}
            >
              {secs}s
            </button>
          ))}
        </div>
        <p className="text-[10px] text-slate-400 mt-1.5">
          A target, not a quota — the cut ends when the point lands. The box above is optional:
          say what the video is for and the edit favours the moments that serve it.
        </p>
      </div>
    </>
  );
};

const StudioComposer = ({
  prompt,
  setPrompt,
  activeFormat,
  setActiveFormat,
  images,
  setImages,
  products,
  setProducts,
  onGenerate,
  videoSlots,
  onVideoSlotFile,
  onVideoSlotClear,
  videoTier,
  setVideoTier,
  videoDuration,
  setVideoDuration,
  videoBusy,
  videoStyle,
  setVideoStyle,
  videoCaptions,
  setVideoCaptions,
  videoSaved,
  onVideoPickSaved,
  videoEffects,
  setVideoEffects,
  videoViralHook,
  setVideoViralHook,
  footageFiles,
  onFootageFile,
  onFootageClear,
  footageSeconds,
  setFootageSeconds,
}) => {
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [imageError, setImageError] = useState('');
  const fmt = findFormat(activeFormat);
  // Footage mode is gated on FILES, not on the prompt — the box is an
  // optional angle there, and requiring text to edit your own video is a
  // dead end with no explanation attached to it.
  const isFootage = fmt.isVideo && videoDuration === 'footage';
  // A design (not a video) can also be generated from catalog picks alone —
  // the product record is the brief.
  const hasProducts = !fmt.isVideo && products.length > 0;
  const canGenerate = isFootage ? footageFiles?.length > 0 : (Boolean(prompt.trim()) || hasProducts);

  useEffect(() => { textareaRef.current?.focus(); }, []);

  // Mirrors FlyerBuilder.handleImageUpload: same limits, same object shape
  // { base64, type, name, preview } so the images pass straight through.
  const MAX_COMPOSER_IMAGES = 4;
  const handleAttach = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    // Catalog picks each bring their own photo and share the same 4 slots.
    const remaining = MAX_COMPOSER_IMAGES - images.length - products.length;
    if (remaining <= 0) { setImageError(`Up to ${MAX_COMPOSER_IMAGES} photos${products.length > 0 ? ', catalog products included' : ''}`); return; }

    const errs = [];
    files.slice(0, remaining).forEach((file) => {
      if (!validTypes.includes(file.type)) { errs.push(`${file.name}: invalid format`); return; }
      if (file.size > 5 * 1024 * 1024) { errs.push(`${file.name}: over 5MB`); return; }
      const reader = new FileReader();
      reader.onload = (ev) => {
        const full = ev.target.result;
        setImages((prev) => prev.length >= MAX_COMPOSER_IMAGES ? prev : [...prev, {
          base64: full.split(',')[1],
          type: file.type,
          name: file.name,
          preview: full,
        }]);
      };
      reader.readAsDataURL(file);
    });
    setImageError(errs.join('. '));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (i) => setImages((prev) => prev.filter((_, idx) => idx !== i));

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      onGenerate();
    }
  };

  // Format slider — all live visual formats (text excluded; SMS-only text
  // sending happens at the send step). Order: send group first (Flyer,
  // Current Deal), then social (IG/FB posts, stories, TikTok, Reel).
  const sliderFormats = FORMATS.filter((f) => f.live && !f.isText);

  const formatScrollRef = useRef(null);
  const scrollFormats = (dir) => {
    const el = formatScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.7), behavior: 'smooth' });
  };

  const dimsLabel = fmt.isText ? 'SMS' : (fmt.width && fmt.height ? `${fmt.width}×${fmt.height}` : null);

  return (
    <div>
      <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight leading-tight mb-1.5">
        What do you want to{' '}
        <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">promote?</span>
      </h2>
      <p className="text-[13px] text-slate-500 mb-5">
        {isFootage
          ? 'Upload the video you already shot — the AI cuts it into a social-ready edit. Say what it’s for below if you want the cut to lean a certain way (optional).'
          : fmt.isVideo
          ? 'Describe the deal, add your face, your store, and the product below — the AI films it, writes the script, and voices it.'
          : 'Describe it, pull a product from your catalog or attach your own photos, and AI designs it. Pick a format below — or leave it on Flyer.'}
      </p>

      {/* THE COMPOSER */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm focus-within:border-blue-400 focus-within:ring-4 focus-within:ring-blue-500/10 transition-all">
        <textarea
          ref={textareaRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={3}
          placeholder={isFootage
            ? 'Optional — what should this cut do? e.g. get people booking Saturday deliveries'
            : 'e.g. Weekend sale — 25% off everything, free delivery within 30 miles'}
          className="w-full resize-none bg-transparent px-4 pt-4 pb-2 text-[15px] leading-relaxed placeholder-slate-400 focus:outline-none"
        />

        {/* Attached image thumbnails */}
        {!fmt.isVideo && images.length > 0 && (
          <div className="flex items-center gap-2 px-4 pb-2 flex-wrap">
            {images.map((img, i) => (
              <div key={i} className="relative group">
                <img src={img.preview} alt={img.name} className="w-12 h-12 rounded-lg object-cover border border-slate-200" />
                <button
                  onClick={() => removeImage(i)}
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-slate-900 text-white flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove"
                >
                  <X className="w-2.5 h-2.5" strokeWidth={2.5} />
                </button>
              </div>
            ))}
          </div>
        )}
        {imageError && (
          <p className="px-4 pb-1 text-[11px] text-red-500">{imageError}</p>
        )}

        {/* Catalog products — search by name or item number. The design is
            built from each pick's photo and facts in the database. Not for
            AI Video: its photos go in the ingredient slots below. */}
        {!fmt.isVideo && (
          <div className="px-3 pb-2">
            <CatalogProductPicker
              products={products}
              onChange={(next) => { setProducts(next); setImageError(''); }}
              max={MAX_COMPOSER_IMAGES - images.length}
            />
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleAttach}
          className="hidden"
        />

        <div className="flex items-center gap-2 px-3 pb-3 pt-1">
          {/* Attach photos — hidden for AI Video: its photos go in the
              ingredient slots below, not the flyer attachment pipeline. */}
          {!fmt.isVideo && (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex-shrink-0 w-9 h-9 rounded-full border border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 flex items-center justify-center transition-all"
            title={`Attach product photos (${images.length}/${MAX_COMPOSER_IMAGES})`}
          >
            <Paperclip className="w-4 h-4" />
          </button>
          )}

          {/* Active-format hint, fills the middle */}
          <div className="flex-1 min-w-0 flex items-center gap-1.5 text-xs text-slate-400 truncate">
            <span className="truncate">
              Designing a <span className="font-semibold text-slate-600">{fmt.label}</span>
              {dimsLabel && dimsLabel !== 'SMS' ? <span className="hidden sm:inline"> · {dimsLabel}</span> : ''}
            </span>
          </div>

          <button
            onClick={onGenerate}
            disabled={!canGenerate || (fmt.isVideo && videoBusy)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl font-semibold text-sm transition-all flex-shrink-0 ${
              !canGenerate
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
            }`}
          >
            <ArrowUp className="w-4 h-4" />
            <span className="hidden sm:inline">{fmt.isVideo && videoBusy ? 'Creating…' : 'Generate'}</span>
          </button>
        </div>
      </div>

      {/* AI VIDEO INGREDIENTS — when the video format is active, the WHOLE
          flow lives on this screen: script in the composer above, the three
          reference photos here, Generate fires the render. Same
          IngredientSlots component the video builder uses. */}
      {fmt.isVideo && (
        <div className="mt-3 rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
          <div className="flex items-center gap-2 mb-1">
            <Film className="w-4 h-4 text-blue-600" />
            <p className="text-sm font-bold text-slate-900">Your ingredients</p>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            {videoDuration === 'footage'
              ? 'Upload what you actually shot — one clip or a few. The AI listens, keeps the strongest moments, cuts the dead air and the “ums”, reframes vertical and captions every word.'
              : 'Add up to 3 photos — or drop a video and we’ll pull the best frame. At least one is needed to generate.'}
          </p>

          {videoDuration === 'footage' ? (
            <FootageUploader
              files={footageFiles}
              onAdd={onFootageFile}
              onClear={onFootageClear}
              disabled={videoBusy}
              seconds={footageSeconds}
              setSeconds={setFootageSeconds}
            />
          ) : (
            <IngredientSlots
              slots={videoSlots}
              onFileSelected={onVideoSlotFile}
              onClear={onVideoSlotClear}
              onPickSaved={onVideoPickSaved}
              saved={videoSaved}
              compact
            />
          )}

          {/* Quality tier — Veo only. A footage edit re-encodes the user's
              own pixels, so there is no model tier to choose. */}
          <div className={`flex gap-2 mt-3 ${videoDuration === 'footage' ? 'hidden' : ''}`}>
            {[
              { id: 'fast', label: 'Fast', sub: '~1–2 min', Icon: Zap },
              { id: 'quality', label: 'Best quality', sub: '~3–6 min', Icon: Gem },
            ].map(({ id, label, sub, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setVideoTier(id)}
                disabled={videoBusy}
                className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-all disabled:opacity-50 ${
                  videoTier === id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${videoTier === id ? 'text-blue-600' : 'text-slate-400'}`} />
                <span className="text-left">
                  <span className={`block text-xs font-bold ${videoTier === id ? 'text-blue-900' : 'text-slate-700'}`}>{label}</span>
                  <span className="block text-[10px] text-slate-400">{sub}</span>
                </span>
              </button>
            ))}
          </div>

          {/* Duration — 16s chains 2 independently-composed scenes (same
              locked identity throughout) instead of one padded-out shot.
              Capped at 2 (not 3): field-tested, a 3rd independent Veo
              generation is where face fidelity started visibly slipping.
              Graphics mode only ever generates ONE Veo scene (the hook);
              everything after is a narrated graphic card, so there's
              exactly one face and one voice to stay consistent. */}
          <div className="flex flex-wrap gap-2 mt-3">
            {[
              // PROMO: reference-mode host take + product cutaways from the
              // real product photo, edited server-side. Needs person + product.
              { id: 'promo', label: 'Promo', sub: 'You + product cutaways' },
              { id: 8, label: '8s', sub: '1 take' },
              { id: 16, label: '16s', sub: 'You, then the product' },
              { id: 'graphics', label: 'Hook + Graphics', sub: 'Dynamic length' },
              // FOOTAGE MODE — nothing is generated: the user's own
              // uploaded video is the material.
              { id: 'footage', label: 'Edit My Video', sub: 'Upload your own' },
            ].map(({ id, label, sub }) => (
              <button
                key={id}
                type="button"
                onClick={() => setVideoDuration(id)}
                disabled={videoBusy}
                className={`flex-1 min-w-[120px] flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-all disabled:opacity-50 ${
                  videoDuration === id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <Clock className={`w-4 h-4 flex-shrink-0 ${videoDuration === id ? 'text-blue-600' : 'text-slate-400'}`} />
                <span className="text-left">
                  <span className={`block text-xs font-bold ${videoDuration === id ? 'text-blue-900' : 'text-slate-700'}`}>{label}</span>
                  <span className="block text-[10px] text-slate-400">{sub}</span>
                </span>
              </button>
            ))}
          </div>
          {videoDuration === 'promo' && (
            <p className="text-[10px] text-slate-400 mt-1.5">
              A real promo edit: you talk to camera the whole take (your face from your photos),
              and the product appears in cutaways cut from its actual photo while your voice keeps
              going. Add a photo of you and the product; extra angles of each make it stronger.
            </p>
          )}
          {videoDuration === 16 && (
            <p className="text-[10px] text-slate-400 mt-1.5">
              Two scenes: 8s of you talking, then 8s of your exact product in your store with the
              rest of your text on screen. Put a line with just <code className="font-mono">---</code>{' '}
              where the switch goes. Needs a photo of you and of the product.
            </p>
          )}
          {videoDuration === 'graphics' && (
            <p className="text-[10px] text-slate-400 mt-1.5">
              {'Add a person photo and the AI films your first line as the hook (add '}
              <code className="font-mono">---</code> and write more lines for graphic scenes after
              it). No person photo? Every scene is a graphic scene — no camera, no character, just
              your voice over premium visuals. Put what's spoken in quotes ("...") and the rest is
              the scene direction, e.g. Show the pricing page. "Plans start at $29 a month." Add
              screenshots of your product to the Product photo slot and each graphic scene shows
              one, composited into a realistic device mockup instead of a plain card.
            </p>
          )}

          {/* Ultra-modern look: style pills + AI keyword captions. All of
              it directs a Veo RENDER, so none of it applies to a footage
              edit — that cut is already captioned and already looks like
              whatever the user shot. */}
          {!isFootage && (
          <VideoStyleControls
            style={videoStyle}
            setStyle={setVideoStyle}
            captions={videoCaptions}
            setCaptions={setVideoCaptions}
            effects={videoEffects}
            setEffects={setVideoEffects}
            viralHook={videoViralHook}
            setViralHook={setVideoViralHook}
            disabled={videoBusy}
          />
          )}

        </div>
      )}

      {/* Format slider — every option visible, scroll for more. The user can
          see at a glance they can design a Flyer, Deal, IG/FB post, Story,
          TikTok or Reel. Selecting one drives the right-pane preview + the
          dimensions the AI designs at. */}
      <div className="mt-3">
        <div className="flex items-center justify-between mb-1.5 px-1">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Format</p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => scrollFormats(-1)}
              aria-label="Scroll formats left"
              className="w-6 h-6 rounded-md border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:bg-slate-50 hover:border-slate-300 transition-all"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => scrollFormats(1)}
              aria-label="Scroll formats right"
              className="w-6 h-6 rounded-md border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:bg-slate-50 hover:border-slate-300 transition-all"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <div
          ref={formatScrollRef}
          className="flex gap-2 overflow-x-auto snap-x scroll-smooth pb-1"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {sliderFormats.map((f) => {
            const Icon = f.icon;
            const active = f.id === activeFormat;
            return (
              <button
                key={f.id}
                onClick={() => setActiveFormat(f.id)}
                className={`group snap-start flex-shrink-0 flex items-center gap-2 pl-1.5 pr-3 py-2 rounded-xl border text-xs font-semibold whitespace-nowrap transition-all ${
                  active
                    ? 'bg-slate-900 border-slate-900 text-white shadow-sm'
                    : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 hover:-translate-y-0.5'
                }`}
              >
                <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${active ? 'bg-white/15' : f.accentIconBg}`}>
                  <Icon className={`w-3.5 h-3.5 ${active ? 'text-white' : f.accentText}`} />
                </span>
                <span className="flex flex-col items-start leading-tight">
                  <span>{f.label}</span>
                  {f.width && f.height && (
                    <span className={`text-[9px] font-mono tabular-nums ${active ? 'text-white/60' : 'text-slate-400'}`}>
                      {f.width}×{f.height}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Hint row */}
      <div className="flex items-center gap-2.5 mt-2 px-1 text-[11px] text-slate-400 flex-wrap">
        <span>
          <kbd className="px-1 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono text-[10px]">⌘</kbd>
          +
          <kbd className="px-1 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono text-[10px]">↵</kbd>
          {' '}to generate
        </span>
      </div>

      {/* Prompt starters */}
      <div className="mt-6">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2.5">Need a spark?</p>
        <div className="flex flex-wrap gap-2">
          {PROMPT_IDEAS.map((idea) => (
            <button
              key={idea}
              onClick={() => { setPrompt(idea); textareaRef.current?.focus(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50/40 text-[12px] text-slate-600 hover:text-slate-900 transition-all"
            >
              <Sparkles className="w-3 h-3 text-blue-400" /> {idea}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================
const MarketingTab = ({ storeId, store, addNotification, hideHeader = false }) => {
  // ----- Loading / mode -----
  const [loading, setLoading] = useState(true);
  const [hasReceiptData, setHasReceiptData] = useState(false);

  // ----- Shared data -----
  const [contacts, setContacts] = useState([]);
  const [sentSurveys, setSentSurveys] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // ----- Drawers (contacts + history only — campaign creation is on-page now) -----
  const [drawerView, setDrawerView] = useState(null); // 'contacts' | 'history' | null
  const openDrawer = (view) => setDrawerView(view);
  const closeDrawer = () => setDrawerView(null);

  // ----- Studio state -----
  // 'picker' = format gallery (Canva-style home); 'design' = AI prompt + preview;
  // 'send'   = post-generation send card (SMS-deliverable formats only).
  const [studioMode, setStudioMode] = useState('picker');
  const [activeFormat, setActiveFormat] = useState('flyer'); // matches FORMATS[].id
  // Picker right-pane preview now tracks the ACTIVE format (selected via the
  // composer chips), not hover. hoveredFormat is retained only if you restore
  // a hover-based gallery later; safe to delete along with FormatGroupRow.
  const [hoveredFormat, setHoveredFormat] = useState(null); // eslint-disable-line no-unused-vars

  // ----- Saved designs -----
  // Recent designs are auto-saved on every successful generation. Surfaced
  // as a horizontal strip on the picker view. Clicking a card sets
  // designToLoad which the FlyerBuilder consumes via its initialDesign prop.
  const [recentDesigns, setRecentDesigns] = useState([]);
  const [loadingDesigns, setLoadingDesigns] = useState(false);
  const [designToLoad, setDesignToLoad] = useState(null);
  // Chat-first composer: the prompt typed on the Studio home, handed to
  // FlyerBuilder when the user hits Generate. Images attached here travel
  // into FlyerBuilder so the first generation designs on top of them.
  const [composerPrompt, setComposerPrompt] = useState('');
  const [composerImages, setComposerImages] = useState([]); // [{base64,type,name,preview}]
  const [composerProducts, setComposerProducts] = useState([]); // catalog picks: [{sku,name,brand,image}]
  // AI Video ingredients live at the STUDIO level (not inside VideoStudio)
  // so the home screen collects them and hands them to the builder on
  // Generate — the whole flow starts from the first screen.
  const [heroVideoSlots, setHeroVideoSlots] = useState({});
  const [heroVideoTier, setHeroVideoTier] = useState('fast');
  const [heroVideoDuration, setHeroVideoDuration] = useState(8);
  // FOOTAGE MODE — the user's own clips and the target cut length.
  const [heroFootageFiles, setHeroFootageFiles] = useState([]);
  const [heroFootageSeconds, setHeroFootageSeconds] = useState(30);
  const [heroVideoStyle, setHeroVideoStyle] = useState('clean');
  const [heroVideoCaptions, setHeroVideoCaptions] = useState(false);
  const [heroVideoEffects, setHeroVideoEffects] = useState(false);
  const [heroVideoViralHook, setHeroVideoViralHook] = useState(false);
  const [heroRecentsToken, setHeroRecentsToken] = useState(0);
  const [videoSlotsToGenerate, setVideoSlotsToGenerate] = useState(null);
  const [videoToShow, setVideoToShow] = useState(null); // mobile: saved video to open in the builder
  const [videoTierToGenerate, setVideoTierToGenerate] = useState(null);
  const [videoDurationToGenerate, setVideoDurationToGenerate] = useState(null);
  const [videoStyleToGenerate, setVideoStyleToGenerate] = useState(null);
  const [videoCaptionsToGenerate, setVideoCaptionsToGenerate] = useState(null);
  const [videoEffectsToGenerate, setVideoEffectsToGenerate] = useState(null);
  const [videoViralHookToGenerate, setVideoViralHookToGenerate] = useState(null);

  // First-screen video generation: the same engine hook VideoStudio uses,
  // rendered into the picker's right-pane phone. Desktop stays on screen 1
  // for the whole compose -> render -> watch flow.
  const heroVideoGen = useVideoGeneration({ storeId, addNotification, onFinished: () => setHeroRecentsToken((t) => t + 1) });
  const heroSavedIngredients = useSavedIngredients(storeId, heroRecentsToken);
  const handleHeroPickSaved = (role, url) => {
    setHeroVideoSlots((prev) => ({ ...prev, [role]: { url, preview: url, reused: true } }));
  };
  const [promptToGenerate, setPromptToGenerate] = useState('');
  const [imagesToGenerate, setImagesToGenerate] = useState([]);
  const [productsToGenerate, setProductsToGenerate] = useState([]);
  // Cap visible designs to RECENT_DESIGNS_VISIBLE; show "Show all" toggle
  // when there are more than that.
  const [recentExpanded, setRecentExpanded] = useState(false);

  // ----- Send state -----
  const [promoMessage, setPromoMessage] = useState('');
  const [flyerImageUrl, setFlyerImageUrl] = useState('');
  const [audienceMode, setAudienceMode] = useState('single'); // 'single' | 'all'
  const [promoPhone, setPromoPhone] = useState('');
  const [isSendingPromo, setIsSendingPromo] = useState(false);
  const [promoSendResult, setPromoSendResult] = useState(null);
  const [promoBlastProgress, setPromoBlastProgress] = useState({ sent: 0, failed: 0, total: 0 });
  const [showBlastConfirm, setShowBlastConfirm] = useState(false);

  // ----- Contacts management -----
  const [addContactName, setAddContactName] = useState('');
  const [addContactPhone, setAddContactPhone] = useState('');
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [addContactResult, setAddContactResult] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [contactSearchQuery, setContactSearchQuery] = useState('');

  // ----- History search -----
  const [historySearchQuery, setHistorySearchQuery] = useState('');

  // ==========================================
  // DATA LOADING
  // ==========================================
  useEffect(() => { if (storeId) loadInitialData(); }, [storeId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const hasReceipts = await detectMode();
      await loadSentHistory(hasReceipts);
    } catch (error) {
      console.error('Error loading marketing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const detectMode = async () => {
    try {
      const { count, error } = await supabase
        .from('receipts')
        .select('id', { count: 'exact', head: true })
        .eq('store_id', storeId)
        .eq('delivery_status', 'delivered');
      if (!error) {
        const hasData = count > 0;
        setHasReceiptData(hasData);
        return hasData;
      }
      return false;
    } catch (err) {
      console.error('Error detecting mode:', err);
      return false;
    }
  };

  const loadSentHistory = async (hasReceipts = false) => {
    try {
      setLoadingHistory(true);
      if (hasReceipts || hasReceiptData) {
        try {
          const { data: surveyReceipts, error } = await supabase
            .from('receipts')
            .select('id, customer_name, customer_phone, customer_satisfaction_score, survey_status, survey_completed_at, review_sms_sent_at, google_review_requested, created_at, survey_feedback')
            .eq('store_id', storeId)
            .not('survey_status', 'is', null)
            .order('review_sms_sent_at', { ascending: false, nullsFirst: false })
            .limit(50);
          if (!error && surveyReceipts) setSentSurveys(surveyReceipts);
        } catch {
          console.log('Receipts not available — standalone mode');
        }
      }
      try {
        const { data: campaignContacts, error: contactsError } = await supabase
          .from('campaign_contacts')
          .select('*')
          .eq('store_id', storeId)
          .order('created_at', { ascending: false })
          .limit(200);
        if (!contactsError && campaignContacts) setContacts(campaignContacts);
      } catch {
        console.log('campaign_contacts table not yet created');
      }
    } catch (err) {
      console.error('Error loading history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // ==========================================
  // AUTH
  // ==========================================
  const getAuthHeaders = async () => {
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    return {
      'Content-Type': 'application/json',
      ...(currentSession?.access_token && { 'Authorization': `Bearer ${currentSession.access_token}` })
    };
  };

  // ==========================================
  // DERIVED
  // ==========================================
  const activeContactsCount = contacts.filter(c => !c.opted_out).length;
  const optedOutCount = contacts.length - activeContactsCount;
  const campaignsSentCount = contacts.reduce((sum, c) => sum + (c.send_count || 0), 0);
  const lastCampaignSent = contacts
    .filter(c => c.last_sent_at)
    .sort((a, b) => new Date(b.last_sent_at) - new Date(a.last_sent_at))[0];

  const getUniquePhones = () => {
    const phoneSet = new Map();
    contacts.forEach(c => {
      if (c.phone && !c.opted_out) {
        const digits = c.phone.replace(/\D/g, '');
        if (digits.length >= 10) {
          phoneSet.set(digits.slice(-10), {
            id: c.id,
            name: c.name || '',
            phone: c.phone,
            source: c.source || 'campaign',
            send_count: c.send_count || 0,
            last_sent_at: c.last_sent_at,
            opted_out: false
          });
        }
      }
    });
    sentSurveys.forEach(s => {
      if (s.customer_phone) {
        const digits = s.customer_phone.replace(/\D/g, '');
        if (digits.length >= 10 && !phoneSet.has(digits.slice(-10))) {
          phoneSet.set(digits.slice(-10), {
            name: s.customer_name || '',
            phone: s.customer_phone,
            source: 'receipt',
            send_count: 0,
            opted_out: false
          });
        }
      }
    });
    return Array.from(phoneSet.values());
  };
  const uniquePhones = getUniquePhones();

  const allHistory = [
    ...sentSurveys.map(s => ({ ...s, _source: 'receipt' })),
    ...contacts.map(c => ({ ...c, _source: 'campaign' }))
  ].sort((a, b) => new Date(b.review_sms_sent_at || b.created_at) - new Date(a.review_sms_sent_at || a.created_at));

  const filteredHistory = historySearchQuery
    ? allHistory.filter(item =>
        (item.customer_name || item.name || '').toLowerCase().includes(historySearchQuery.toLowerCase()) ||
        (item.customer_phone || item.phone || '').includes(historySearchQuery.replace(/\D/g, ''))
      )
    : allHistory;

  const filteredContacts = contactSearchQuery
    ? contacts.filter(c =>
        (c.name || '').toLowerCase().includes(contactSearchQuery.toLowerCase()) ||
        (c.phone || '').includes(contactSearchQuery.replace(/\D/g, ''))
      )
    : contacts;

  // ==========================================
  // STUDIO FLOW
  // ==========================================
  const startOver = () => {
    setStudioMode('design');
    setFlyerImageUrl('');
    setPromoMessage('');
    setPromoPhone('');
    setPromoSendResult(null);
    setAudienceMode('single');
    setShowBlastConfirm(false);
  };

  // Picker → Design: user picked a format card.
  const handleSelectFormat = (format) => {
    setActiveFormat(format.id);
    setDesignToLoad(null);  // fresh format pick — don't restore a previous design
    setFlyerImageUrl('');
    setPromoMessage('');
    setStudioMode('design');
  };

  // Hero ingredient-slot handlers — same processor the video builder uses,
  // so photo/video handling is byte-identical on both screens.
  const handleHeroVideoSlotFile = async (role, file) => {
    try {
      const processed = await processIngredientFile(file);
      setHeroVideoSlots((prev) => ({ ...prev, [role]: processed }));
    } catch (err) {
      if (addNotification) addNotification(err.message || 'Could not process that file', 'error');
    }
  };

  const handleHeroVideoSlotClear = (role) => {
    setHeroVideoSlots((prev) => {
      const next = { ...prev };
      if (next[role]?.preview?.startsWith('blob:')) URL.revokeObjectURL(next[role].preview);
      delete next[role];
      return next;
    });
  };

  const handleHeroFootageFile = (file) => {
    try {
      const processed = processFootageFile(file);
      setHeroFootageFiles((prev) => (
        prev.length >= MAX_FOOTAGE_FILES ? prev : [...prev, processed]
      ));
    } catch (err) {
      if (addNotification) addNotification(err.message || 'Could not add that video', 'error');
    }
  };

  const handleHeroFootageClear = (index) => {
    setHeroFootageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Chat-first composer → Design: user typed a prompt (and maybe attached
  // product photos) on the Studio home and hit Generate. Routes to the
  // builder for the active format — FlyerBuilder consumes promptToGenerate +
  // imagesToGenerate and auto-fires one generation; VideoStudio consumes
  // promptToGenerate as its prefilled description (no auto-fire — a video
  // needs reference photos and costs real money per run).
  const handleComposerGenerate = () => {
    const text = composerPrompt.trim();

    // FOOTAGE MODE runs entirely on this screen and needs no prompt — the
    // uploaded clips are the input, and the box is an optional angle. It
    // also never hands off to the builder on mobile the way a generated
    // video does: the files live in THIS component's state, so handing
    // off would arrive with nothing to edit.
    if (activeFormatObj?.isVideo && heroVideoDuration === 'footage') {
      if (!heroFootageFiles.length) {
        if (addNotification) addNotification('Add at least one video to edit', 'error');
        return;
      }
      heroVideoGen.generate({
        duration: 'footage',
        footageFiles: heroFootageFiles,
        footageSeconds: heroFootageSeconds,
        description: text,
        businessName: store?.name || 'Our Business',
      }).then((r) => {
        if (r && !r.ok && r.error && addNotification) addNotification(r.error, 'error');
      });
      return;
    }

    // Catalog picks alone are enough for a design; a video still needs text.
    const picks = activeFormatObj?.isVideo ? [] : composerProducts;
    if (!text && picks.length === 0) return;

    if (activeFormatObj?.isVideo) {
      const wide = typeof window !== 'undefined'
        && window.matchMedia
        && window.matchMedia('(min-width: 1024px)').matches;
      if (wide) {
        // Stay on the first screen — the right-pane phone becomes the player.
        heroVideoGen.generate({
          description: text,
          slots: heroVideoSlots,
          tier: heroVideoTier,
          duration: heroVideoDuration,
          style: heroVideoStyle,
          captions: heroVideoCaptions,
          effects: heroVideoEffects,
          viralHook: heroVideoViralHook,
          businessName: store?.name || 'Our Business',
        }).then((r) => {
          if (r && !r.ok && r.error && addNotification) addNotification(r.error, 'error');
        });
        return;
      }
      // Mobile: no right pane in the picker — hand off to the builder view.
      setVideoTierToGenerate(heroVideoTier);
      setVideoDurationToGenerate(heroVideoDuration);
      setVideoStyleToGenerate(heroVideoStyle);
      setVideoCaptionsToGenerate(heroVideoCaptions);
      setVideoEffectsToGenerate(heroVideoEffects);
      setVideoViralHookToGenerate(heroVideoViralHook);
    }

    setDesignToLoad(null);
    setFlyerImageUrl('');
    setPromoMessage('');
    setPromptToGenerate(text);
    setImagesToGenerate(composerImages);
    setProductsToGenerate(picks);
    setVideoSlotsToGenerate(
      activeFormatObj?.isVideo && Object.keys(heroVideoSlots).length > 0
        ? heroVideoSlots
        : null
    );
    setStudioMode('design');
  };

  // Design → Picker: "All formats" / "Change format" button.
  const handleBackToPicker = () => {
    setStudioMode('picker');
    setDesignToLoad(null);
    setPromptToGenerate('');
    setImagesToGenerate([]);
    setProductsToGenerate([]);
    setVideoSlotsToGenerate(null);
    setVideoTierToGenerate(null);
    setVideoDurationToGenerate(null);
    setVideoStyleToGenerate(null);
    setVideoCaptionsToGenerate(null);
    setVideoEffectsToGenerate(null);
    setVideoViralHookToGenerate(null);
    setVideoToShow(null);
    setComposerImages([]);
    setComposerProducts([]);
    setFlyerImageUrl('');
    setPromoMessage('');
    setPromoPhone('');
    setPromoSendResult(null);
    setAudienceMode('single');
    setShowBlastConfirm(false);
    // Refresh the recent strip so a design just generated shows up
    loadRecentDesigns();
  };

  // ==========================================
  // SAVED DESIGNS — fetch / load / delete
  // ==========================================
  const loadRecentDesigns = async () => {
    if (!storeId) return;
    setLoadingDesigns(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) { setRecentDesigns([]); return; }

      const resp = await fetch(`/api/designs?store_id=${encodeURIComponent(storeId)}&limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) {
        console.warn('Recent designs fetch failed:', resp.status);
        setRecentDesigns([]);
        return;
      }
      const data = await resp.json();
      setRecentDesigns(Array.isArray(data?.designs) ? data.designs : []);
    } catch (e) {
      console.warn('Recent designs error:', e?.message || e);
      setRecentDesigns([]);
    } finally {
      setLoadingDesigns(false);
    }
  };

  // Initial load + refresh after a generation completes (we re-call this
  // from handleBackToPicker so the strip reflects the latest auto-save).
  useEffect(() => { if (storeId) loadRecentDesigns(); }, [storeId]);

  // Click a saved design card → restore it into the builder. We rebuild the
  // format object from the saved snapshot so old designs still load even if
  // the format catalog has shifted since.
  const handleLoadDesign = (design) => {
    if (!design) return;
    const matchedFormat = FORMATS.find(f => f.id === design.format_id);
    // If the format still exists in the catalog, use the canonical record;
    // otherwise reconstruct one from the saved snapshot.
    const formatToUse = matchedFormat || {
      id: design.format_id,
      label: design.format_label,
      width: design.format_width,
      height: design.format_height,
      aspectRatio: design.format_aspect_ratio,
      platform: design.platform,
      deliveryMode: design.delivery_mode,
      live: true,
    };
    setActiveFormat(formatToUse.id);
    setDesignToLoad(design);
    setFlyerImageUrl('');
    setPromoMessage('');
    setStudioMode('design');
  };

  const handleDeleteDesign = async (designId, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    if (!designId) return;
    // Optimistic remove
    setRecentDesigns(prev => prev.filter(d => d.id !== designId));
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;
      await fetch(`/api/designs?id=${encodeURIComponent(designId)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) {
      console.warn('Delete failed:', err?.message || err);
      // Refetch so UI reconciles if the delete actually didn't go through
      loadRecentDesigns();
    }
  };

  // Resolved active-format object (rich record from catalog)
  const activeFormatObj = findFormat(activeFormat);

  const handleFlyerReady = (imageUrl) => {
    // Only SMS-deliverable formats transition to send mode. Social formats
    // download directly inside FlyerBuilder and stay on the design surface.
    if (activeFormatObj?.deliveryMode !== 'sms') return;
    setFlyerImageUrl(imageUrl);
    setStudioMode('send');
    setAudienceMode(uniquePhones.length > 0 ? 'all' : 'single');
  };

  const handleTextPromoReady = () => {
    if (!promoMessage.trim()) return;
    setStudioMode('send');
    setAudienceMode(uniquePhones.length > 0 ? 'all' : 'single');
  };

  // ==========================================
  // SEND
  // ==========================================
  const sendPromoToPhone = async (phone) => {
    const e164 = getE164Phone(phone);
    const authHeaders = await getAuthHeaders();
    if (!activeFormatObj.isText && flyerImageUrl) {
      const response = await fetch('/api/campaign-send-mms', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          phone: e164,
          message: promoMessage.trim() || 'Check out our latest promotion!',
          media_url: flyerImageUrl,
          store_id: storeId,
          campaign_type: 'promo_mms',
          source: 'promo_mms'
        })
      });
      return response.json();
    }
    const fullMessage = `${promoMessage.trim()}${LEGAL_FOOTER}`;
    const response = await fetch('/api/campaign-send', {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        phone: e164,
        message: fullMessage,
        store_id: storeId,
        campaign_type: 'promo_sms',
        source: 'promo_sms'
      })
    });
    return response.json();
  };

  const handleSendPromoSingle = async () => {
    if (!isValidPhone(promoPhone)) {
      setPromoSendResult({ type: 'error', message: 'Please enter a valid 10-digit phone number' });
      setTimeout(() => setPromoSendResult(null), 4000);
      return;
    }
    setIsSendingPromo(true);
    setPromoSendResult(null);
    try {
      const data = await sendPromoToPhone(promoPhone);
      if (data.success) {
        setPromoSendResult({ type: 'success', message: `Sent to ${promoPhone}!${data.note ? ` (${data.note})` : ''}` });
        setPromoPhone('');
        loadSentHistory();
        if (addNotification) addNotification(`Promo sent to ${promoPhone}`, 'success');
      } else {
        setPromoSendResult({ type: 'error', message: data.error || 'Failed to send' });
      }
    } catch (err) {
      console.error('Promo send error:', err);
      setPromoSendResult({ type: 'error', message: 'Network error — try again' });
    } finally {
      setIsSendingPromo(false);
      setTimeout(() => setPromoSendResult(null), 5000);
    }
  };

  const handleSendPromoBlast = async () => {
    if (uniquePhones.length === 0) return;
    setIsSendingPromo(true);
    setPromoSendResult(null);
    setShowBlastConfirm(false);
    setPromoBlastProgress({ sent: 0, failed: 0, total: uniquePhones.length });

    let sent = 0;
    let failed = 0;

    for (const contact of uniquePhones) {
      try {
        const data = await sendPromoToPhone(contact.phone);
        if (data.success) sent++; else failed++;
      } catch { failed++; }
      setPromoBlastProgress({ sent, failed, total: uniquePhones.length });
      await new Promise(resolve => setTimeout(resolve, 250));
    }

    setPromoSendResult({
      type: failed === uniquePhones.length ? 'error' : 'success',
      message: `Sent ${sent} of ${uniquePhones.length}${failed > 0 ? ` (${failed} failed)` : ''}`
    });
    setIsSendingPromo(false);
    loadSentHistory();
    if (addNotification) addNotification(`Blast: ${sent}/${uniquePhones.length} sent`, sent > 0 ? 'success' : 'error');
    setTimeout(() => setPromoSendResult(null), 8000);
  };

  // ==========================================
  // CONTACTS
  // ==========================================
  const handleAddContact = async () => {
    if (!isValidPhone(addContactPhone)) {
      setAddContactResult({ type: 'error', message: 'Enter a valid 10-digit phone number' });
      setTimeout(() => setAddContactResult(null), 4000);
      return;
    }
    setIsAddingContact(true);
    setAddContactResult(null);
    try {
      const e164 = getE164Phone(addContactPhone);
      const { error } = await supabase
        .from('campaign_contacts')
        .upsert({
          store_id: storeId,
          phone: e164,
          name: addContactName.trim(),
          source: 'manual',
          send_count: 0
        }, { onConflict: 'store_id,phone' });
      if (error) throw error;
      setAddContactResult({ type: 'success', message: `${addContactName || e164} added!` });
      setAddContactName('');
      setAddContactPhone('');
      loadSentHistory();
      if (addNotification) addNotification('Contact saved', 'success');
    } catch (err) {
      console.error('Error adding contact:', err);
      setAddContactResult({ type: 'error', message: err.message || 'Failed to add contact' });
    } finally {
      setIsAddingContact(false);
      setTimeout(() => setAddContactResult(null), 5000);
    }
  };

  const handleImportFromReceipts = async () => {
    setIsImporting(true);
    setImportResult(null);
    try {
      const { data, error } = await supabase.rpc('import_receipt_contacts', { p_store_id: storeId });
      if (error) {
        const { data: receipts, error: fetchErr } = await supabase
          .from('receipts')
          .select('customer_name, customer_phone')
          .eq('store_id', storeId)
          .eq('delivery_status', 'delivered')
          .not('customer_phone', 'is', null);
        if (fetchErr) throw fetchErr;
        let imported = 0;
        const seen = new Set();
        for (const r of (receipts || [])) {
          if (!r.customer_phone || seen.has(r.customer_phone)) continue;
          seen.add(r.customer_phone);
          const e164 = getE164Phone(r.customer_phone);
          const { error: upsertErr } = await supabase
            .from('campaign_contacts')
            .upsert({
              store_id: storeId,
              phone: e164,
              name: r.customer_name || '',
              source: 'receipt_import'
            }, { onConflict: 'store_id,phone' });
          if (!upsertErr) imported++;
        }
        setImportResult({ type: 'success', message: `Imported ${imported} contacts from receipts` });
      } else {
        setImportResult({ type: 'success', message: `Imported ${data || 0} contacts from receipts` });
      }
      loadSentHistory();
      if (addNotification) addNotification('Receipt contacts imported!', 'success');
    } catch (err) {
      console.error('Import error:', err);
      setImportResult({ type: 'error', message: err.message || 'Import failed' });
    } finally {
      setIsImporting(false);
      setTimeout(() => setImportResult(null), 6000);
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      const { error } = await supabase
        .from('campaign_contacts')
        .delete()
        .eq('id', contactId)
        .eq('store_id', storeId);
      if (error) throw error;
      setContacts(prev => prev.filter(c => c.id !== contactId));
      if (addNotification) addNotification('Contact removed', 'success');
    } catch (err) {
      console.error('Error deleting contact:', err);
      if (addNotification) addNotification('Failed to remove contact', 'error');
    }
  };

  const handleToggleOptOut = async (contactId, currentOptedOut) => {
    try {
      const updates = { opted_out: !currentOptedOut };
      if (!currentOptedOut) updates.opted_out_at = new Date().toISOString();
      const { error } = await supabase
        .from('campaign_contacts')
        .update(updates)
        .eq('id', contactId)
        .eq('store_id', storeId);
      if (error) throw error;
      setContacts(prev => prev.map(c => c.id === contactId ? { ...c, ...updates } : c));
      if (addNotification) addNotification(!currentOptedOut ? 'Contact opted out' : 'Contact opted back in', 'success');
    } catch (err) {
      console.error('Error toggling opt-out:', err);
    }
  };

  // ==========================================
  // LOADING
  // ==========================================
  if (loading) {
    return (
      <div className="flex items-center justify-center p-16 bg-slate-50 h-full">
        <div className="flex flex-col items-center gap-3 text-slate-400">
          <RefreshCw className="w-6 h-6 animate-spin" />
          <span className="text-sm font-medium">Loading…</span>
        </div>
      </div>
    );
  }

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <div
      className={hideHeader
        ? "h-full flex flex-col overflow-hidden bg-slate-50"
        : "h-full flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 shadow-[0_1px_3px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.12)] bg-white"}
      style={hideHeader
        // Mobile dashboard: the parent (fixed inset-0 → flex-1 scroll area →
        // h-full) already bounds our height exactly. A 100vh-based cap here is
        // WRONG on iOS — 100vh is the LARGE viewport (URL bar collapsed), so
        // the cap sat taller than the real screen and, with overflow-hidden,
        // clipped the bottom of the studio under the nav with no way to scroll.
        ? undefined
        : { maxHeight: 'calc(100vh - 4rem)' }}
    >

      {/* ==========================================
          HEADER — logo on left, stats + actions on right
          Stats compacted to inline pills (no longer in main area)
      ========================================== */}
      {!hideHeader && (
        <div className="flex-shrink-0 bg-gradient-to-b from-white to-slate-50/80 backdrop-blur-xl p-4 border-b border-slate-200 sticky top-0 z-10 shadow-[0_1px_2px_rgba(15,23,42,0.03)]">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                Marketing <span className="text-blue-600">Studio</span>
              </h2>
              <p className="text-slate-500 text-xs mt-0.5">AI-designed promos to your customers</p>
            </div>

            {/* Compact stats + actions */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {/* Stats pills (hidden on mobile to save space) */}
              <div className="hidden md:flex items-center gap-1.5 mr-1 pr-2 border-r border-slate-200">
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200">
                  <Users className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-[11px] font-semibold text-slate-500">Reach</span>
                  <span className="text-[12px] font-bold text-slate-900 tabular-nums">{uniquePhones.length}</span>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200">
                  <Send className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-[11px] font-semibold text-slate-500">Sent</span>
                  <span className="text-[12px] font-bold text-slate-900 tabular-nums">{campaignsSentCount}</span>
                </div>
                {lastCampaignSent && (
                  <button
                    onClick={() => openDrawer('history')}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200 hover:bg-slate-100 hover:border-slate-300 transition-all"
                    title={`Last sent ${lastCampaignSent.name || lastCampaignSent.phone}`}
                  >
                    <Clock className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-[11px] font-semibold text-slate-500">Last</span>
                    <span className="text-[12px] font-bold text-slate-900">{formatDate(lastCampaignSent.last_sent_at)}</span>
                  </button>
                )}
              </div>

              <button
                onClick={() => openDrawer('contacts')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold text-xs transition-all"
              >
                <Users className="w-3.5 h-3.5 text-slate-500" />
                <span className="hidden sm:inline">Contacts</span>
                <span className="px-1.5 py-0.5 rounded-md bg-slate-100 text-[10px] font-bold text-slate-600">
                  {activeContactsCount}
                </span>
              </button>
              <button
                onClick={() => openDrawer('history')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold text-xs transition-all"
              >
                <History className="w-3.5 h-3.5 text-slate-500" />
                <span className="hidden sm:inline">History</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==========================================
          MAIN — Studio: picker → design → send
          No vertical scrolling on this surface; each pane handles its own overflow.
      ========================================== */}
      <div className={`flex-1 overflow-hidden flex flex-col min-h-0 ${hideHeader ? '' : 'bg-slate-100/70 shadow-[inset_0_2px_4px_-2px_rgba(15,23,42,0.06)]'}`}>
        <style>{`
          .studio-scroll { scrollbar-width: thin; scrollbar-color: rgb(203 213 225) transparent; }
          .studio-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
          .studio-scroll::-webkit-scrollbar-track { background: transparent; }
          .studio-scroll::-webkit-scrollbar-thumb { background: rgb(203 213 225); border-radius: 9999px; border: 2px solid transparent; background-clip: content-box; }
          .studio-scroll::-webkit-scrollbar-thumb:hover { background: rgb(148 163 184); background-clip: content-box; }
        `}</style>

        {/* ============ PICKER ============ */}
        {studioMode === 'picker' && (
          <div className="flex-1 overflow-y-auto studio-scroll px-4 py-5">
            <div className="w-full">

              {/* Two-column layout: format options on left, canvas/shape preview on right */}
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,3fr)] gap-6 lg:gap-8 mb-6 items-start">

                {/* ============ LEFT: Chat-first composer ============ */}
                <div>
                  <StudioComposer
                    prompt={composerPrompt}
                    setPrompt={setComposerPrompt}
                    activeFormat={activeFormat}
                    setActiveFormat={setActiveFormat}
                    images={composerImages}
                    setImages={setComposerImages}
                    products={composerProducts}
                    setProducts={setComposerProducts}
                    onGenerate={handleComposerGenerate}
                    videoSlots={heroVideoSlots}
                    onVideoSlotFile={handleHeroVideoSlotFile}
                    onVideoSlotClear={handleHeroVideoSlotClear}
                    videoTier={heroVideoTier}
                    setVideoTier={setHeroVideoTier}
                    videoDuration={heroVideoDuration}
                    setVideoDuration={setHeroVideoDuration}
                    footageFiles={heroFootageFiles}
                    onFootageFile={handleHeroFootageFile}
                    onFootageClear={handleHeroFootageClear}
                    footageSeconds={heroFootageSeconds}
                    setFootageSeconds={setHeroFootageSeconds}
                    videoBusy={heroVideoGen.phase === 'generating'}
                    videoStyle={heroVideoStyle}
                    setVideoStyle={setHeroVideoStyle}
                    videoCaptions={heroVideoCaptions}
                    setVideoCaptions={setHeroVideoCaptions}
                    videoSaved={heroSavedIngredients}
                    onVideoPickSaved={handleHeroPickSaved}
                    videoEffects={heroVideoEffects}
                    setVideoEffects={setHeroVideoEffects}
                    videoViralHook={heroVideoViralHook}
                    setVideoViralHook={setHeroVideoViralHook}
                  />
                </div>

                {/* ============ RIGHT: Phone mockup workspace ============ */}
                <div className="hidden lg:block">
                  <div className="sticky top-2 bg-gradient-to-b from-slate-50 to-white border border-slate-200 rounded-2xl p-5 flex flex-col overflow-hidden shadow-[0_1px_2px_rgba(15,23,42,0.04),0_1px_3px_rgba(15,23,42,0.06)]" style={{ height: 'min(88vh, 1150px)' }}>
                    <div className="flex items-center justify-between mb-4 flex-shrink-0">
                      <div className="flex items-center gap-1.5">
                        <div className="w-4 h-4 rounded bg-slate-100 border border-slate-200 flex items-center justify-center">
                          <Sparkles className="w-2.5 h-2.5 text-slate-500" />
                        </div>
                        <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Preview</h3>
                      </div>
                    </div>

                    {/* Phone mockup — fills available height; caption pinned below.
                        Structure + height model mirror FlyerBuilder's split exactly
                        so the handoff to design mode doesn't shift the phone. */}
                    <div className="flex-1 min-h-0 flex flex-col items-center py-4 overflow-hidden">
                      <div className="flex-1 min-h-0 w-full">
                        <ScaleToFit baseWidth={300} baseHeight={620} maxScale={1.75}>
                          {activeFormatObj.isVideo && heroVideoGen.phase !== 'compose'
                            ? <HeroVideoPhone gen={heroVideoGen} businessName={store?.name} />
                            : <DevicePreview format={activeFormatObj} />}
                        </ScaleToFit>
                      </div>

                      {/* Format label + dimensions caption (below the device) */}
                      <div className="mt-5 text-center flex-shrink-0">
                        <p className="text-sm font-bold text-slate-900">{activeFormatObj.label}</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">{activeFormatObj.sublabel}</p>
                        {activeFormatObj.width && activeFormatObj.height && (
                          <p className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-[10px] text-slate-600 font-mono tabular-nums">
                            {activeFormatObj.width} × {activeFormatObj.height}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* LIBRARY — every past creation in one place, grouped by what
                  it is: Videos, Flyers & deals, Social posts. Click any item
                  to reopen it (videos play in the preview phone). */}
              <div className="mt-8 pt-4 border-t border-slate-200/70">
                <div className="flex items-center justify-between mb-2.5 px-1">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3 h-3 text-slate-400" />
                    <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                      Saved designs
                    </h3>
                  </div>
                  {loadingDesigns && (
                    <RefreshCw className="w-3 h-3 text-slate-300 animate-spin" />
                  )}
                </div>

                {/* Videos */}
                <div className="mb-3">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 px-1">Videos</p>
                  <CaptionsHealthBanner />
                  <RecentVideosStrip
                    embedded
                    storeId={storeId}
                    refreshToken={heroRecentsToken}
                    onPlay={(url) => {
                      setActiveFormat('ai-video');
                      const wide = typeof window !== 'undefined'
                        && window.matchMedia
                        && window.matchMedia('(min-width: 1024px)').matches;
                      if (wide) {
                        // Desktop: play right in the preview phone.
                        heroVideoGen.showVideo(url);
                      } else {
                        // Mobile: the picker has no right pane — open the
                        // builder with this video playing at the top.
                        setVideoToShow(url);
                        setStudioMode('design');
                      }
                    }}
                    notify={addNotification}
                  />
                </div>

                {(() => {
                  const isSend = (d) => d.format_id === 'flyer' || d.format_id === 'current-deal';
                  const sendDesigns = recentDesigns.filter(isSend);
                  const socialDesigns = recentDesigns.filter((d) => !isSend(d));
                  return (
                    <>
                      {sendDesigns.length > 0 && (
                        <div className="mb-3">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 px-1">Flyers &amp; deals</p>
                          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'thin' }}>
                            {sendDesigns.map((d) => (
                              <SavedDesignCard key={d.id} d={d} onLoad={handleLoadDesign} onDelete={handleDeleteDesign} />
                            ))}
                          </div>
                        </div>
                      )}

                      {socialDesigns.length > 0 && (
                        <div className="mb-1">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 px-1">Social posts</p>
                          <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'thin' }}>
                            {socialDesigns.map((d) => (
                              <SavedDesignCard key={d.id} d={d} onLoad={handleLoadDesign} onDelete={handleDeleteDesign} />
                            ))}
                          </div>
                        </div>
                      )}

                      {recentDesigns.length === 0 && (
                        <div className="rounded-lg border border-dashed border-slate-200 bg-white px-3 py-4 text-center">
                          <p className="text-[11px] text-slate-500">
                            Designs you create will appear here. Click any to reopen and edit.
                          </p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Trust footer */}
              <div className="hidden md:flex items-center justify-center gap-1.5 text-[11px] text-slate-400 mt-6 pt-4 border-t border-slate-200/70">
                <Shield className="w-3 h-3 flex-shrink-0" />
                <span>Opted-out contacts auto-excluded · STOP/HELP keywords handled by carrier</span>
              </div>
            </div>
          </div>
        )}

        {/* ============ DESIGN ============ */}
        {studioMode === 'design' && (
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="px-4 pt-5 pb-4 w-full flex-1 min-h-0 flex flex-col">

            {/* Format breadcrumb is rendered INSIDE FlyerBuilder's left column
                (via the leftHeader prop) so it consumes left-column space rather
                than pushing the whole body down — keeping the workspace card top
                aligned with the picker's preview card. */}

            {/* The studio body — split layout */}
            <div className="flex-1 min-h-0">
              {!activeFormatObj.isText && !activeFormatObj.isVideo && (
                <FlyerBuilder
                  storeId={storeId}
                  businessName={store?.name || 'Our Business'}
                  businessInfo={store?.address || ''}
                  onFlyerReady={handleFlyerReady}
                  addNotification={addNotification}
                  variant="split"
                  format={activeFormatObj}
                  initialDesign={designToLoad}
                  initialPrompt={promptToGenerate}
                  initialImages={imagesToGenerate}
                  initialProducts={productsToGenerate}
                  onPromptConsumed={() => { setPromptToGenerate(''); setImagesToGenerate([]); setProductsToGenerate([]); }}
                  leftHeader={(
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <button
                        onClick={handleBackToPicker}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all group"
                      >
                        <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
                        <span>All formats</span>
                      </button>
                      <div className="w-px h-4 bg-slate-200" />
                      <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white border border-slate-200">
                        <div className={`w-5 h-5 rounded-md ${activeFormatObj.accentIconBg} border border-slate-200 flex items-center justify-center flex-shrink-0`}>
                          {(() => { const Icon = activeFormatObj.icon; return <Icon className={`w-3 h-3 ${activeFormatObj.accentText}`} />; })()}
                        </div>
                        <span className="text-xs font-bold text-slate-900">{activeFormatObj.label}</span>
                        {activeFormatObj.width && activeFormatObj.height && (
                          <span className="hidden sm:inline text-[10px] text-slate-400 font-mono tabular-nums">{activeFormatObj.width}×{activeFormatObj.height}</span>
                        )}
                      </div>
                    </div>
                  )}
                />
              )}

              {activeFormatObj.isText && (
                <div className="flex flex-col h-full min-h-0">
                  <div className="flex items-center gap-2.5 flex-wrap mb-3">
                    <button
                      onClick={handleBackToPicker}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all group"
                    >
                      <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
                      <span>All formats</span>
                    </button>
                    <div className="w-px h-4 bg-slate-200" />
                    <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white border border-slate-200">
                      <div className={`w-5 h-5 rounded-md ${activeFormatObj.accentIconBg} border border-slate-200 flex items-center justify-center flex-shrink-0`}>
                        {(() => { const Icon = activeFormatObj.icon; return <Icon className={`w-3 h-3 ${activeFormatObj.accentText}`} />; })()}
                      </div>
                      <span className="text-xs font-bold text-slate-900">{activeFormatObj.label}</span>
                    </div>
                  </div>
                  <div className="flex-1 min-h-0">
                    <TextPromoSplit
                      promoMessage={promoMessage}
                      setPromoMessage={setPromoMessage}
                      onContinue={handleTextPromoReady}
                    />
                  </div>
                </div>
              )}

              {activeFormatObj.isVideo && (
                <div className="flex flex-col h-full min-h-0">
                  <div className="flex items-center gap-2.5 flex-wrap mb-3">
                    <button
                      onClick={handleBackToPicker}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-all group"
                    >
                      <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
                      <span>All formats</span>
                    </button>
                    <div className="w-px h-4 bg-slate-200" />
                    <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white border border-slate-200">
                      <div className={`w-5 h-5 rounded-md ${activeFormatObj.accentIconBg} border border-slate-200 flex items-center justify-center flex-shrink-0`}>
                        {(() => { const Icon = activeFormatObj.icon; return <Icon className={`w-3 h-3 ${activeFormatObj.accentText}`} />; })()}
                      </div>
                      <span className="text-xs font-bold text-slate-900">{activeFormatObj.label}</span>
                    </div>
                  </div>
                  <div className="flex-1 min-h-0">
                    <VideoStudio
                      storeId={storeId}
                      businessName={store?.name || 'Our Business'}
                      addNotification={addNotification}
                      initialPrompt={promptToGenerate}
                      initialSlots={videoSlotsToGenerate}
                      initialTier={videoTierToGenerate}
                      initialDuration={videoDurationToGenerate}
                      initialStyle={videoStyleToGenerate}
                      initialCaptions={videoCaptionsToGenerate}
                      initialEffects={videoEffectsToGenerate}
                      initialViralHook={videoViralHookToGenerate}
                      initialShowUrl={videoToShow}
                      onShowConsumed={() => setVideoToShow(null)}
                      onPromptConsumed={() => { setPromptToGenerate(''); setImagesToGenerate([]); setVideoSlotsToGenerate(null); setVideoTierToGenerate(null); setVideoDurationToGenerate(null); setVideoStyleToGenerate(null); setVideoCaptionsToGenerate(null); setVideoEffectsToGenerate(null); setVideoViralHookToGenerate(null); }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Trust footer — single line, unobtrusive */}
            <div className="hidden md:flex flex-shrink-0 items-center justify-center gap-1.5 text-[11px] text-slate-400 mt-3 pt-3 border-t border-slate-200/70">
              <Shield className="w-3 h-3 flex-shrink-0" />
              <span>Opted-out contacts auto-excluded · STOP/HELP keywords handled by carrier</span>
            </div>
            </div>
          </div>
        )}

        {studioMode === 'send' && (
          <div className="flex-1 min-h-0 overflow-y-auto studio-scroll">
            <div className="px-4 py-4 space-y-4 w-full">

              {/* Top row — back link + ready badge */}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <button
                  onClick={startOver}
                  className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 font-semibold transition-all group"
                >
                  <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
                  <span>Design another</span>
                </button>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-bold text-emerald-700 uppercase tracking-wider">
                  <CheckCircle className="w-3 h-3" />
                  <span>Ready to send</span>
                </div>
              </div>

              {/* Two-column: preview + send */}
              <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">

                {/* LEFT: preview */}
                <div className="space-y-2">
                  {!activeFormatObj.isText && flyerImageUrl && (
                    <>
                      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                        <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-3 flex justify-center">
                          <img
                            src={flyerImageUrl}
                            alt="Flyer preview"
                            className="rounded-lg shadow-md w-full object-contain object-top"
                            style={{ maxHeight: '380px' }}
                          />
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          try {
                            const res = await fetch(flyerImageUrl);
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `flyer_${Date.now()}.png`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            setTimeout(() => URL.revokeObjectURL(url), 1000);
                          } catch (err) {
                            console.error('Download error:', err);
                            if (addNotification) addNotification('Failed to download', 'error');
                          }
                        }}
                        className="flex items-center justify-center gap-1.5 w-full py-2 rounded-lg bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-600 hover:text-slate-900 text-xs font-semibold transition-all"
                      >
                        <Download className="w-3.5 h-3.5" />
                        <span>Download PNG</span>
                      </button>
                    </>
                  )}

                  {activeFormatObj.isText && (
                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-7 h-7 rounded-lg bg-sky-50 border border-sky-200 flex items-center justify-center">
                          <MessageSquare className="w-3.5 h-3.5 text-sky-600" />
                        </div>
                        <span className="text-xs font-bold text-slate-900">SMS Preview</span>
                      </div>
                      <div className="bg-slate-50 rounded-xl border border-slate-200 p-3 text-[13px] text-slate-700 leading-relaxed">
                        <p className="whitespace-pre-wrap">{promoMessage}</p>
                        <p className="mt-2 text-[11px] text-slate-400 italic">Reply STOP to opt out. Reply HELP for help.</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* RIGHT: send controls */}
                <div className="space-y-3">

                  <div className="space-y-1">
                    <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                      {activeFormatObj.isText ? 'Your message is ready' : `Your ${activeFormatObj.label.toLowerCase()} is looking sharp`}
                    </h2>
                    <p className="text-xs text-slate-500">Pick who gets it. We&apos;ll handle the delivery.</p>
                  </div>

                  {/* Caption (flyer only) */}
                  {!activeFormatObj.isText && (
                    <div className="bg-white border border-slate-200 rounded-2xl p-3.5">
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
                        Caption <span className="text-slate-400 font-medium normal-case tracking-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={promoMessage}
                        onChange={(e) => setPromoMessage(e.target.value)}
                        placeholder="e.g. Don&rsquo;t miss our weekend sale!"
                        className="w-full px-3.5 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        maxLength={300}
                      />
                      <p className="text-[11px] text-slate-400 mt-1.5">Sends with the flyer image as MMS</p>
                    </div>
                  )}

                  {/* Audience picker */}
                  <div className="bg-white border border-slate-200 rounded-2xl p-3.5 space-y-3">
                    <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Send to</h3>

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => { setAudienceMode('single'); setShowBlastConfirm(false); }}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          audienceMode === 'single' ? 'border-blue-600 bg-blue-50/50' : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Phone className={`w-4 h-4 mb-1.5 ${audienceMode === 'single' ? 'text-blue-600' : 'text-slate-400'}`} />
                        <p className={`text-xs font-bold ${audienceMode === 'single' ? 'text-slate-900' : 'text-slate-600'}`}>One person</p>
                        <p className="text-[11px] text-slate-500">Test it first</p>
                      </button>
                      <button
                        onClick={() => { setAudienceMode('all'); setShowBlastConfirm(false); }}
                        disabled={uniquePhones.length === 0}
                        className={`p-3 rounded-xl border-2 text-left transition-all ${
                          uniquePhones.length === 0 ? 'border-slate-200 opacity-50 cursor-not-allowed' :
                          audienceMode === 'all' ? 'border-blue-600 bg-blue-50/50' : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Users className={`w-4 h-4 mb-1.5 ${audienceMode === 'all' ? 'text-blue-600' : 'text-slate-400'}`} />
                        <p className={`text-xs font-bold ${audienceMode === 'all' ? 'text-slate-900' : 'text-slate-600'}`}>Everyone</p>
                        <p className="text-[11px] text-slate-500">{uniquePhones.length} {uniquePhones.length === 1 ? 'person' : 'people'}</p>
                      </button>
                    </div>

                    {audienceMode === 'single' && (
                      <PhoneInputWithSend
                        phone={promoPhone}
                        setPhone={setPromoPhone}
                        onSend={handleSendPromoSingle}
                        sending={isSendingPromo}
                        result={promoSendResult}
                      />
                    )}

                    {audienceMode === 'all' && (
                      <div className="space-y-2.5">
                        {uniquePhones.length === 0 ? (
                          <div className="p-3 bg-slate-50 border border-dashed border-slate-300 rounded-lg text-center">
                            <p className="text-xs font-semibold text-slate-700 mb-1">No contacts yet</p>
                            <p className="text-[11px] text-slate-500 mb-2">Add some to send to your full list</p>
                            <button
                              onClick={() => openDrawer('contacts')}
                              className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                            >
                              Manage contacts →
                            </button>
                          </div>
                        ) : (
                          <>
                            {isSendingPromo && (
                              <div className="space-y-1.5">
                                <div className="flex items-center justify-between text-[11px] text-slate-600 font-semibold">
                                  <span className="flex items-center gap-1.5"><RefreshCw className="w-3 h-3 animate-spin" />Sending…</span>
                                  <span>{promoBlastProgress.sent + promoBlastProgress.failed} / {promoBlastProgress.total}</span>
                                </div>
                                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                                  <div
                                    style={{ width: `${((promoBlastProgress.sent + promoBlastProgress.failed) / promoBlastProgress.total) * 100}%` }}
                                    className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full transition-all"
                                  />
                                </div>
                              </div>
                            )}

                            <ResultBanner result={promoSendResult} />

                            {!showBlastConfirm ? (
                              <button
                                onClick={() => setShowBlastConfirm(true)}
                                disabled={isSendingPromo}
                                className={`w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                                  isSendingPromo ? 'bg-slate-200 text-slate-400 cursor-not-allowed' :
                                  'bg-gradient-to-br from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-600/20'
                                }`}
                              >
                                <Send className="w-4 h-4" />
                                <span>Send to all {uniquePhones.length} contacts</span>
                              </button>
                            ) : (
                              <div className="p-3 bg-amber-50 border-2 border-amber-300 rounded-lg space-y-2.5">
                                <div className="flex items-start gap-2">
                                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs font-bold text-amber-900">Confirm send</p>
                                    <p className="text-[11px] text-amber-800 mt-0.5">
                                      This will send to <strong>{uniquePhones.length}</strong> {uniquePhones.length === 1 ? 'person' : 'people'} immediately.
                                    </p>
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => setShowBlastConfirm(false)}
                                    disabled={isSendingPromo}
                                    className="flex-1 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 font-semibold text-xs hover:bg-slate-50 transition-all disabled:opacity-50"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={handleSendPromoBlast}
                                    disabled={isSendingPromo}
                                    className="flex-1 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs flex items-center justify-center gap-1.5 transition-all disabled:opacity-50"
                                  >
                                    {isSendingPromo ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                    <span>{isSendingPromo ? 'Sending…' : 'Confirm'}</span>
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Trust note */}
                  <div className="flex items-start gap-1.5 text-[11px] text-slate-400 px-1">
                    <Shield className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>
                      {activeFormatObj.isText
                        ? 'STOP/HELP footer auto-appended. STOP replies excluded by carrier.'
                        : `Sent as MMS with ${activeFormatObj.label.toLowerCase()} image. STOP/HELP footer auto-appended.`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ==========================================
          DRAWER: CONTACTS
      ========================================== */}
      <Drawer
        open={drawerView === 'contacts'}
        onClose={closeDrawer}
        title="Contacts"
        subtitle={`${activeContactsCount} active${optedOutCount > 0 ? ` · ${optedOutCount} opted out` : ''}`}
        icon={Users}
        iconTone="blue"
        width="xl"
      >
        <div className="space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Add contact</p>
            <div className="space-y-2">
              <input
                type="text"
                value={addContactName}
                onChange={(e) => setAddContactName(e.target.value)}
                placeholder="Name (optional)"
                className="w-full px-3.5 py-2.5 rounded-lg bg-white border border-slate-200 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
              />
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="tel"
                    value={addContactPhone}
                    onChange={(e) => setAddContactPhone(formatPhoneInput(e.target.value))}
                    onKeyDown={(e) => { if (e.key === 'Enter' && isValidPhone(addContactPhone)) handleAddContact(); }}
                    placeholder="(555) 123-4567"
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-white border border-slate-200 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                    maxLength={14}
                  />
                </div>
                <button
                  onClick={handleAddContact}
                  disabled={isAddingContact || !isValidPhone(addContactPhone)}
                  className={`px-4 py-2.5 rounded-lg font-semibold text-sm flex items-center gap-1.5 transition-all ${
                    isAddingContact || !isValidPhone(addContactPhone)
                      ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                  }`}
                >
                  {isAddingContact ? <RefreshCw className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  <span className="hidden sm:inline">Add</span>
                </button>
              </div>
              <ResultBanner result={addContactResult} />
            </div>
          </div>

          {hasReceiptData && (
            <div className="bg-white border border-slate-200 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Download className="w-4 h-4 text-slate-500" />
                  <span className="text-xs font-semibold text-slate-700">Import from receipts</span>
                </div>
                <button
                  onClick={handleImportFromReceipts}
                  disabled={isImporting}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
                    isImporting ? 'bg-slate-200 text-slate-400' : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {isImporting ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                  <span>{isImporting ? 'Importing…' : 'Import'}</span>
                </button>
              </div>
              {importResult && <div className="mt-2"><ResultBanner result={importResult} /></div>}
            </div>
          )}

          {contacts.length > 5 && (
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={contactSearchQuery}
                onChange={(e) => setContactSearchQuery(e.target.value)}
                placeholder="Search contacts…"
                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
              />
              {contactSearchQuery && (
                <button onClick={() => setContactSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200">
                  <X className="w-3 h-3 text-slate-500" />
                </button>
              )}
            </div>
          )}

          {contacts.length === 0 ? (
            <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-8 text-center">
              <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-sm font-semibold text-slate-700">No contacts yet</p>
              <p className="text-xs text-slate-500 mt-1">Add one above or import from receipts</p>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
              <p className="text-xs text-slate-500">No matches for &ldquo;{contactSearchQuery}&rdquo;</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {filteredContacts.map((contact) => (
                <div
                  key={contact.id}
                  className={`bg-white border rounded-xl p-3 transition-all ${
                    contact.opted_out ? 'border-red-200 bg-red-50/30' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between min-w-0 gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm font-bold text-slate-900 truncate">{contact.name || 'Unnamed'}</p>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0 ${
                          contact.source === 'manual' ? 'bg-blue-50 border border-blue-200 text-blue-700' :
                          contact.source === 'receipt_import' ? 'bg-slate-100 border border-slate-200 text-slate-600' :
                          contact.source === 'review' ? 'bg-amber-50 border border-amber-200 text-amber-700' :
                          contact.source === 'promo_sms' ? 'bg-sky-50 border border-sky-200 text-sky-700' :
                          contact.source === 'promo_mms' ? 'bg-blue-50 border border-blue-200 text-blue-700' :
                          'bg-slate-100 border border-slate-200 text-slate-600'
                        }`}>
                          {contact.source === 'manual' ? 'MANUAL' :
                            contact.source === 'receipt_import' ? 'IMPORT' :
                            contact.source === 'review' ? 'REVIEW' :
                            contact.source === 'promo_sms' ? 'SMS' :
                            contact.source === 'promo_mms' ? 'MMS' :
                            (contact.source || 'SAVED').toUpperCase()}
                        </span>
                        {contact.opted_out && (
                          <span className="px-1.5 py-0.5 bg-red-100 border border-red-300 rounded text-[10px] text-red-700 font-bold flex-shrink-0">OPTED OUT</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-[11px] text-slate-500 font-mono">{contact.phone}</p>
                        {contact.send_count > 0 && (
                          <span className="text-[11px] text-slate-400">
                            · {contact.send_count} {contact.send_count === 1 ? 'msg' : 'msgs'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleToggleOptOut(contact.id, contact.opted_out)}
                        title={contact.opted_out ? 'Opt back in' : 'Opt out'}
                        className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
                          contact.opted_out
                            ? 'bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-600'
                            : 'bg-slate-50 hover:bg-red-50 border border-slate-200 hover:border-red-200 text-slate-400 hover:text-red-500'
                        }`}
                      >
                        <Ban className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteContact(contact.id)}
                        title="Remove"
                        className="w-7 h-7 rounded-lg bg-slate-50 hover:bg-red-50 border border-slate-200 hover:border-red-200 flex items-center justify-center text-slate-400 hover:text-red-500 transition-all"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Drawer>

      {/* ==========================================
          DRAWER: HISTORY
      ========================================== */}
      <Drawer
        open={drawerView === 'history'}
        onClose={closeDrawer}
        title="Send History"
        subtitle={`${allHistory.length} ${allHistory.length === 1 ? 'item' : 'items'}`}
        icon={History}
        iconTone="slate"
        width="xl"
      >
        <div className="space-y-3">
          {allHistory.length > 5 && (
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={historySearchQuery}
                onChange={(e) => setHistorySearchQuery(e.target.value)}
                placeholder="Search history…"
                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-white border border-slate-200 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
              />
              {historySearchQuery && (
                <button onClick={() => setHistorySearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200">
                  <X className="w-3 h-3 text-slate-500" />
                </button>
              )}
            </div>
          )}

          {loadingHistory ? (
            <div className="p-8 flex items-center justify-center">
              <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-8 text-center">
              <p className="text-xs text-slate-500">
                {historySearchQuery ? `No matches for "${historySearchQuery}"` : 'No history yet'}
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {filteredHistory.map((item) => (
                <div
                  key={item.id}
                  className="bg-white border border-slate-200 rounded-xl p-3 hover:border-slate-300 transition-all"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold text-slate-900 truncate">{item.customer_name || item.name || 'Unknown'}</p>
                        {item._source === 'receipt' && (
                          <span className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[10px] text-slate-600 font-bold">ORDER</span>
                        )}
                        {item.google_review_requested && (
                          <span className="px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 rounded text-[10px] text-emerald-700 font-bold">GOOGLE ✓</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-[11px] text-slate-500 font-mono">{item.customer_phone || item.phone || '—'}</p>
                        <span className="text-[11px] text-slate-400">·</span>
                        <span className="text-[11px] text-slate-400">
                          {formatDate(item.review_sms_sent_at || item.created_at)}
                        </span>
                      </div>
                    </div>
                    <div className="flex-shrink-0">{getSurveyStatusBadge(item)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default MarketingTab;